/*
 * <nc-fitting-room> — the virtual fitting room as one standalone component (migration steps 1 to 3, Phase A1).
 * Reads and writes only through NcApp.gateway() (the mock in A1): getCurrentLook, getProduct, addLookItem, removeLookItem, saveLook,
 * shareLook, getAsset and, in mode "user" only, getTryOnConfig. No network of its own, no persistence, no images of its own.
 *
 * What it owns (extracted from the SCR-010 controller, behaviour unchanged): the look model — `look` is the list of garments and
 * every entry point (chooser chip through apply(), row x, undo, redo, clear) goes through changeLook(), which updates the model
 * optimistically, re-renders ALL views at once (item list, total, count, canvas message, chooser chips through the
 * `nc-fitting:change` event, tools) and calls the gateway; a refusal rolls back exactly the operations that failed, re-renders and
 * says so (toast + live region). It also owns the canvas (nc-tryon), the look panel (nc-current-look), the side tools, the model
 * selector (chips from presets, or "Cambiar modelo" with the bottom sheet below 700 px: PENDING visual validation by Javi, PV-11),
 * the "Ver más" collapse of the look panel (PV-12) and save / share / add / connect. The chooser (catalogue, collections) is the
 * host's: it lives in the `[data-fr-chooser]` slot and talks to the component through apply() / toggle() and the change event.
 *
 * Configuration: assets/nc-fitting-room-presets.js (`preset` attribute, overridable attribute by attribute: mode, model-source,
 * tools, actions, item-fields, views, chooser-position, head, add-position, look-limit). Layout: container queries on the
 * component's own width (>= 1100 three columns chooser | canvas | look, 700 to 1099 two columns, below stacked).
 * Mode "brand" (decision (a) of Javi) makes no render, quota or consent call and needs no session; mode "user" reads the quota and
 * the garment limit from gateway.getTryOnConfig() (never hardcoded; the gateway refuses a garment over the limit and the toast
 * shows its message).
 *
 * Render (step 1.5, user mode only; every call lives in createRenderFlow, which does nothing when Presets.usesRender is false): MANUAL
 * "Generar" button (provisional, not in Figma: data-pending-visual). Canvas idle -> loading -> rendered | error through nc-tryon.
 * Session mandatory (unauthenticated -> cancelable `nc-fitting:signin`, no routing), provisional consent sheet before the first render
 * (getTryOnConsent / setTryOnConsent), quota and garment limit from getTryOnConfig() and the render response, status ready / failed /
 * blocked with "Reintentar", "Imagen generada con IA" notice, the generated image saved WITH the look (saveLook({ renderId }), PV-4).
 * The avatar photo is Phase B (picker): the host gives its upload id through setAvatar(id) or the `avatar-upload-id` attribute; without
 * one the gateway's own "Sube primero tu foto" is shown and `nc-fitting:model` {action:'upload'} fires.
 * Public API (step 1.6): window.NcFittingRoom.add(productId, options), see below.
 *
 * Bloque 3 states (seven ChatGPT images of 2026-10-24, the source of truth; copy PROVISIONAL, all through data-msg-states / consent):
 * the canvas stage carries `data-fr-state` = upload | generating | error | quota (priority in that order) and shows the matching overlay:
 * upload (drop area, file input, validateAvatar), generating (violet bar + derived percentage), error (centred card, retry / use the last
 * result) and quota (centred card, dots from getTryOnConfig().dailyLookQuota). GENERAR lives in the canvas footer (PV-14); the consent is a modal (>= 700 px) or a bottom sheet; the model panel is a bottom sheet (mobile) or side panel
 * (tablet) with a 2 x 2 grid of models. (D-PROB-5: the URL review parameters are gone, the real backend answers; historical) Reproducing each state in the A1 mock (user mode): ?nc-quota=used (quota), ?nc-render-polls=30
 * + GENERAR (generating), ?nc-render=failed|blocked + GENERAR (error), "Cambiar modelo" or GENERAR without a photo (upload; a file named
 * invalid-*.jpg fails validateAvatar), GENERAR with a photo and no consent yet (consent).
 *
 * Brand mode = MODEL-LOOK VIEWER (PV-10, preset brand-corner, combine off): choosing a model loads HER look from
 * gateway.getBrandModelLook(brand, modelId) (example data in A1), shown as the garment list with size and total plus "Ver y conectar
 * productos" (event only); the list is read-only and the current look (getCurrentLook) is not used.
 * Brand mode (confirmed 22 Oct 21:39): the chosen model's own photo with the look's garments laid out one by one over it
 * ([data-fr-garments], decorative); `combine` (attribute, preset brand-corner = "off") keeps add / remove / undo / redo (history, no buttons) available
 * behind configuration for when users may combine garments in brand mode too: combine="on" is the only switch.
 * Tanda A of the probador (Javi 2026-10-25, docs/normcore-probador-ux-leader.md §4-§6), attribute looks="on" (user mode only): CREAR MI LOOK
 * ENQUEUES a look in <nc-looks> (assets/nc-looks.js) and never locks the builder (A5: `rendering` stays false, the look keeps changing while
 * the render runs; the same garments already being created are not sent twice and an already created look turns the button into
 * "Ver mi look"); every look is a persistent card of the gallery (A4) that opens in the viewer (A3), inline in the stage from 1000 px of the
 * component (WIDE, the two-column desktop); the stage shows the example placeholder (A2) until there is one and no overlay of the old canvas
 * states. Tanda 1 of docs/normcore-probador-revalidacion.md: below the desktop the stage previews the newest look (nc-looks setPreviewHost:
 * creating figure + live time copy, then its picture), CREAR carries the same time copy with three dots while its garments are being
 * created (paintWaitLabel), the empty slot row is one line and the filled one has ONE "+", the stage follows the builder in the DOM on the
 * desktop (orderBuilder) and only a keyboard create moves the focus to the result (focusReadyLook); the quota line only
 * shows with 1 or 0 renders left (DP-6, showQuota); the photo dialog carries the consent checkbox and says "Usar y crear" from CREAR (A6).
 * A refused request (quota, session, consent, photo) leaves no card and keeps its existing notice; failed / blocked leave an error card with
 * "Reintentar" (no quota spent). Extension points for Fase B: DP-2 (a saved avatar: setAvatar(uploadId) already skips the photo step) and DP-4
 * (proposed looks: replaceLook(ids) is the one entry point a "completa el look" source would call).
 * Events (bubbling): nc-fitting:change {items, pending}, :save {lookId}, :share {lookId, url}, :connect {items} (cancelable),
 * :open {kind, id} (cancelable), :model {modelId | action} (cancelable), :tool {id} (cancelable), :quota {left, max},
 * :render {status: 'ready' | 'failed' | 'blocked' | 'refused', renderId, assetId, reason?}, :signin {reason} (cancelable),
 * and on document: :added {productId, status} (from NcFittingRoom.add). Navigation stays the host's (Phase B). Pure helpers are
 * exported for node:test.
 */
(function () {
  var Presets = typeof module !== 'undefined' && module.exports ? require('./nc-fitting-room-presets.js') : window.NcFittingRoomPresets;
  // B3: the preset models of the photo step (rules, the asset as a File, the remembered model) live in assets/nc-preset-models.js
  // (read when used: a host may load this file before the snippet loads nc-preset-models.js; defer keeps their order inside the snippet only)
  var ModelsRequired = typeof module !== 'undefined' && module.exports ? require('./nc-preset-models.js') : null;
  function presetModels() { return ModelsRequired || (typeof window !== 'undefined' ? window.NcPresetModels : null) || null; }

  // "59,99 €" -> 5999 (cents); anything that is not a price is null
  function parsePrice(text) {
    var m = /(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d{1,2}))?/.exec(String(text == null ? '' : text));
    if (!m) return null;
    var whole = parseInt(m[1].replace(/\./g, ''), 10);
    var dec = m[2] ? parseInt((m[2] + '0').slice(0, 2), 10) : 0;
    return whole * 100 + dec;
  }

  function formatPrice(cents) {
    var n = Math.max(0, Math.round(cents));
    var whole = String(Math.floor(n / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var dec = String(n % 100);
    if (dec.length < 2) dec = '0' + dec;
    return whole + ',' + dec + ' €';
  }

  // D-PROB-4: the ONE price format, "24,99 €", whatever the source wrote ("€24,99", "24,99 EUR"); text that is not a price stays as it is
  function normalizePrice(text) {
    var cents = parsePrice(text);
    return cents === null ? String(text == null ? '' : text) : formatPrice(cents);
  }

  // D-PROB-6 (assumed, Javi can change it): "Ir a la tienda" of the exhausted-quota card goes to the brand's own page when the room lives on a
  // brand corner (/collections/<brand>?view=marca: stay there, close the sheet, bring the garments up), else to the brands hub
  function storeTarget(pathname, search) {
    return /^\/collections\/[^/]+/.test(String(pathname || '')) && /[?&]view=marca\b/.test(String(search || '')) ? 'here' : 'hub';
  }

  // total of the readable prices; null when none is readable
  function totalPrice(prices) {
    var sum = 0;
    var any = false;
    prices.forEach(function (p) {
      var c = parsePrice(p);
      if (c !== null) { sum += c; any = true; }
    });
    return any ? sum : null;
  }

  // "{n} prenda/{n} prendas": first form for exactly 1, second otherwise
  function plural(pattern, n) {
    var parts = String(pattern || '').split('/');
    return parts.length > 1 && n !== 1 ? parts[1] : parts[0];
  }

  function fill(pattern, vars) {
    return String(pattern || '').replace(/\{(\w+)\}/g, function (all, k) { return vars[k] === undefined ? all : String(vars[k]); });
  }

  // ---- look history (undo / redo): entries are { op: 'add' | 'remove', items: [product] } ----
  function inverseOp(op) { return op === 'add' ? 'remove' : 'add'; }
  function historyEmpty() { return { undo: [], redo: [] }; }
  function historyPush(h, entry) { return { undo: h.undo.concat([entry]).slice(-50), redo: [] }; }
  function historyUndo(h) {
    if (!h.undo.length) return null;
    var entry = h.undo[h.undo.length - 1];
    return { entry: entry, history: { undo: h.undo.slice(0, -1), redo: h.redo.concat([entry]) } };
  }
  function historyRedo(h) {
    if (!h.redo.length) return null;
    var entry = h.redo[h.redo.length - 1];
    return { entry: entry, history: { undo: h.undo.concat([entry]), redo: h.redo.slice(0, -1) } };
  }

  // the slot a garment takes in the look ('top', 'bottom', ... or 'other'); the gateway classifies, the component only compares
  function slotOf(p) { return p && p.slot && p.slot !== 'other' ? p.slot : ''; }

  // ONE garment per body slot (2026-10-24): the garment of the same slot already in the look, if any ('other' never clashes)
  function slotClash(look, product) {
    var slot = slotOf(product);
    if (!slot) return null;
    for (var i = 0; i < look.length; i++) if (look[i].id !== product.id && slotOf(look[i]) === slot) return look[i];
    return null;
  }

  // plan the operations of a change against the current look: skips what is already (add) or not (remove) in it. An add whose slot is
  // taken REPLACES that garment in place: the action carries `replaces`, so the look keeps its size and the limit is not touched
  function planOps(look, ops) {
    var work = look.slice();
    var plan = [];
    ops.forEach(function (op) {
      var id = op.product ? op.product.id : op.id;
      var at = work.map(function (p) { return p.id; }).indexOf(id);
      if (op.op === 'add' && at < 0) {
        var clash = slotClash(work, op.product);
        if (clash) {
          var ci = work.indexOf(clash);
          work.splice(ci, 1, op.product);
          plan.push({ op: 'add', product: op.product, index: ci, replaces: clash });
        } else {
          var to = typeof op.index === 'number' ? Math.max(0, Math.min(op.index, work.length)) : work.length;
          work.splice(to, 0, op.product);
          plan.push({ op: 'add', product: op.product, index: to });
        }
      }
      if (op.op === 'remove' && at >= 0) { plan.push({ op: 'remove', product: work[at], index: at }); work.splice(at, 1); }
    });
    return plan;
  }

  // a history entry from an applied plan: products in plan order and the positions they had (removals) or took (additions)
  function makeEntry(op, plan) {
    return { op: op, items: plan.map(function (a) { return a.product; }), indexes: plan.map(function (a) { return a.index; }), replaced: plan.map(function (a) { return a.replaces || null; }), ops: plan.map(function (a) { return a.op; }) };
  }

  // "Probar este look" (UX audit 2026-10-25): the operations that turn the current look into `products` (a diff: garments shared by both
  // stay where they are). Removals first, so the new garments never clash with the ones that leave; the order of the new look is kept.
  function planReplace(look, products) {
    var next = (products || []).filter(Boolean);
    var keep = {};
    next.forEach(function (p) { keep[p.id] = true; });
    var ops = look.filter(function (p) { return !keep[p.id]; }).map(function (p) { return { op: 'remove', id: p.id }; });
    var have = {};
    look.forEach(function (p) { have[p.id] = true; });
    next.forEach(function (p) { if (!have[p.id]) ops.push({ op: 'add', product: p, id: p.id }); });
    return ops;
  }

  // operations that undo an entry: a removal is undone by re-adding in REVERSE order at the original positions (D-07)
  function undoOps(entry) {
    var items = entry.items.map(function (p, k) { return { product: p, index: entry.indexes ? entry.indexes[k] : undefined }; });
    // a replaced look comes back in one step: what was added leaves, what was removed returns at its place (reverse order)
    if (entry.op === 'replace') {
      return items.map(function (it, k) { return { it: it, op: entry.ops[k], back: entry.replaced && entry.replaced[k] }; }).reverse().map(function (x) {
        if (x.op === 'remove') return { op: 'add', product: x.it.product, id: x.it.product.id, index: x.it.index };
        return x.back ? { op: 'add', product: x.back, id: x.back.id, index: x.it.index } : { op: 'remove', id: x.it.product.id };
      });
    }
    if (entry.op === 'remove') {
      return items.reverse().map(function (it) { return { op: 'add', product: it.product, id: it.product.id, index: it.index }; });
    }
    // an add that replaced a garment is undone by adding that garment back: its slot is taken by the new one, so it replaces it in place
    return items.map(function (it, k) {
      var back = entry.replaced && entry.replaced[k];
      return back ? { op: 'add', product: back, id: back.id, index: it.index } : { op: 'remove', id: it.product.id };
    });
  }

  // operations that apply an entry again
  function redoOps(entry) {
    return entry.items.map(function (p, k) {
      var op = entry.op === 'replace' ? entry.ops[k] : entry.op;
      return op === 'remove' ? { op: 'remove', id: p.id } : { op: 'add', product: p, id: p.id };
    });
  }

  // category error copy: list is [forbidden title, body, conflict title, body, server title, body, sign-in title, body, sign-in button]
  function errorCopy(kind, list) {
    var at = { forbidden: 0, conflict: 2, server: 4, unauthenticated: 6 }[kind];
    if (at === undefined || !list || !list[at]) return null;
    return { title: list[at], body: list[at + 1] || '' };
  }

  // undo the operations that the gateway refused (ok[i] false) on a copy of the look
  function rollbackOps(look, plan, ok) {
    var out = look.slice();
    plan.forEach(function (a, i) {
      if (ok[i]) return;
      if (a.op === 'add') {
        var k = out.map(function (p) { return p.id; }).indexOf(a.product.id);
        if (k >= 0 && a.replaces) out.splice(k, 1, a.replaces);
        else if (k >= 0) out.splice(k, 1);
      } else if (!out.some(function (p) { return p.id === a.product.id; })) {
        out.splice(Math.min(a.index, out.length), 0, a.product);
      }
    });
    return out;
  }

  // "Ver más" (PV-12): how many rows stay visible when the look panel is collapsed and how many the control reveals.
  // limit 0 turns the collapse off. A look that fits in the limit never shows the control.
  function collapsePlan(count, limit) {
    var n = Math.max(0, Math.floor(count) || 0);
    var l = Math.max(0, Math.floor(limit) || 0);
    if (!l || n <= l) return { shown: n, hidden: 0 };
    return { shown: l, hidden: n - l };
  }

  // the second line of a look row: the size when the item has one and "size" is a field, else the price (the gateway gives no size yet)
  function rowDetail(item, fields, sizePattern) {
    if (fields.indexOf('size') >= 0 && item && item.size) return fill(sizePattern || 'Talla {size}', { size: item.size });
    if (fields.indexOf('price') >= 0 || fields.indexOf('size') >= 0) return (item && item.price) || '';
    return '';
  }


  // ---- try-on render flow (step 1.5; PV-2 to PV-8). The ONLY place of this file that touches the render, consent or quota methods
  // of the gateway, and every method starts with the `enabled` guard: brand mode (usesRender false) makes no such call at all. ----
  function firstMessage(fieldErrors) {
    var k = fieldErrors && Object.keys(fieldErrors)[0];
    return k ? String(fieldErrors[k]) : '';
  }

  function consentOk(config, consent) {
    return !!(consent && consent.granted && config && consent.version === config.consentVersion);
  }

  // gateway failure -> outcome the component can show; nothing here navigates
  function classifyRenderError(err) {
    var kind = err && err.kind;
    var fields = (err && err.fieldErrors) || {};
    if (kind === 'unauthenticated') return { status: 'unauthenticated', message: (err && err.message) || '' };
    if (kind === 'forbidden') return { status: 'consent-required', message: (err && err.message) || '' };
    if (kind === 'validation' && fields.quota) return { status: 'quota', message: fields.quota, left: 0 };
    if (kind === 'validation') return { status: 'invalid', message: firstMessage(fields), fields: fields };
    return { status: 'error', kind: kind || 'unknown', message: (err && err.message) || '', retryable: !(err && err.retryable === false) };
  }

  // Generating (state 7a): the render contract (getLookRender) has no percentage, only queued | processing | ready | failed | blocked.
  // The shown percentage is DERIVED from the poll count: poll n of the ~20 seconds the copy promises (3 % at the start, never above 95 %
  // until the render is ready). It is an estimate, not a measure; Phase B can replace it if the backend reports progress.
  function progressPercent(n, pollMs, expectedMs) {
    var total = expectedMs || 20000;
    var step = pollMs > 0 ? pollMs : 450;
    return Math.min(95, Math.max(3, Math.round(((n + 1) * step) / total * 100)));
  }

  // DP-6 (Javi 2026-10-25): the daily quota is only shown when 1 or 0 renders are left; unknown (null) shows nothing
  function showQuota(left) { return typeof left === 'number' && left <= 1; }

  function createRenderFlow(gw, cfg, opts) {
    opts = opts || {};
    var pollMs = opts.pollMs === undefined ? 450 : opts.pollMs;
    var maxPolls = opts.maxPolls || 40;
    var enabled = !!gw && !!cfg && Presets.usesRender(cfg) && typeof gw.requestLookRender === 'function';
    var flow = { enabled: enabled, config: null, left: null, max: null, quota: '', session: null };
    var off = function () { return Promise.resolve({ status: 'off' }); };
    var wait = function (ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); };

    flow.load = function () {
      if (!enabled) return off();
      var auth = gw.getAuthState ? gw.getAuthState().then(function (a) { flow.session = a && a.status; }, function () { flow.session = null; }) : Promise.resolve();
      return auth.then(function () { return gw.getTryOnConfig(); }).then(function (c) {
        flow.config = c;
        flow.left = c.rendersLeftToday;
        flow.max = c.maxGarmentsPerLook;
        flow.quota = c.dailyLookQuota;
        return { status: 'ok', config: c, left: flow.left, max: flow.max, session: flow.session };
      }, function (err) { return classifyRenderError(err); });
    };

    flow.needsSession = function () { return !!(flow.config ? flow.config.requiresSession : true) && flow.session !== null && flow.session !== 'authenticated'; };

    // A6: whether the consent is already recorded (the photo dialog shows its checkbox only when it is not)
    flow.consentGiven = function () {
      if (!enabled || !gw.getTryOnConsent) return Promise.resolve(false);
      return (flow.config ? Promise.resolve(flow.config) : gw.getTryOnConfig()).then(function (c) {
        flow.config = c;
        return gw.getTryOnConsent().then(function (consent) { return consentOk(c, consent); });
      });
    };

    flow.acceptConsent = function () {
      if (!enabled) return off();
      return (flow.config ? Promise.resolve(flow.config) : gw.getTryOnConfig()).then(function (c) {
        flow.config = c;
        return gw.setTryOnConsent({ granted: true, version: c.consentVersion });
      }).then(function () { return { status: 'ok' }; }, classifyRenderError);
    };

    flow.generate = function (input) {
      if (!enabled) return off();
      var ids = ((input && input.productIds) || []).slice();
      var session = gw.getAuthState ? gw.getAuthState().then(function (a) { flow.session = a && a.status; }, function () {}) : Promise.resolve();
      return session.then(function () {
        if (flow.needsSession()) return { status: 'unauthenticated', message: '' };
        return (flow.config ? Promise.resolve(flow.config) : gw.getTryOnConfig()).then(function (c) {
          flow.config = c;
          return gw.getTryOnConsent().then(function (consent) {
            if (!consentOk(c, consent)) return { status: 'consent-required', message: '' };
            return gw.requestLookRender({ avatarUploadId: input && input.avatarUploadId, productIds: ids }).then(function (res) {
              flow.left = res.rendersLeftToday;
              // N10: the render is requested now (after the photo check): the looks' wait clock starts here, not at CREAR
              if (input && typeof input.onRequested === 'function') input.onRequested(res);
              return poll(res.renderId, 0, input && input.onProgress);
            });
          });
        });
      }).then(function (r) { if (r.left === undefined && flow.left !== null) r.left = flow.left; return r; }, function (err) {
        var r = classifyRenderError(err);
        if (r.status === 'quota') flow.left = 0;
        if (flow.left !== null) r.left = flow.left;
        return r;
      });
    };

    // a render that fails or is blocked does not use up the day's quota (Javi, 2026-10-23; the gateway gives it back): the count follows
    function giveBack() { if (flow.left !== null && flow.left !== undefined) flow.left = flow.quota ? Math.min(flow.quota, flow.left + 1) : flow.left + 1; }

    function poll(renderId, n, onProgress) {
      return gw.getLookRender(renderId).then(function (r) {
        if (r.status === 'ready') return { status: 'ready', renderId: renderId, assetId: r.assetId || null, aiGenerated: r.aiGenerated !== false };
        if (r.status === 'failed' || r.status === 'blocked') { giveBack(); return { status: r.status, renderId: renderId, assetId: null, errorCode: r.errorCode || '' }; }
        if (r.status !== 'queued' && r.status !== 'processing') return { status: 'failed', renderId: renderId, assetId: null, errorCode: 'unknown_status' };
        if (n >= maxPolls) return { status: 'failed', renderId: renderId, assetId: null, errorCode: 'timeout' };
        if (typeof onProgress === 'function') onProgress(progressPercent(n, pollMs));
        return wait(pollMs).then(function () { return poll(renderId, n + 1, onProgress); });
      });
    }

    return flow;
  }

  // ---- public add (step 1.6): the current look through the gateway, same limit rule, no navigation ----
  function addToLook(gw, productId, options) {
    options = options || {};
    var done = function (status, extra) { return Object.assign({ status: status, productId: productId }, extra || {}); };
    if (!gw || typeof gw.addLookItem !== 'function' || typeof gw.getCurrentLook !== 'function' || !productId) return Promise.resolve(done('unavailable'));
    return gw.getCurrentLook().then(function (look) {
      if ((look.items || []).some(function (i) { return i.productId === productId; })) return done('exists');
      return gw.addLookItem({ productId: productId, variantId: options.variantId }).then(function (res) {
        // the gateway replaced the garment of the same body slot (rule: one garment per slot): say which, and offer to put it back
        var rep = res && res.status === 'replaced' && res.replaced ? res.replaced : null;
        if (!rep) return done('added');
        var mine = ((res && res.items) || []).filter(function (i) { return i.productId === productId; })[0];
        return done('replaced', {
          replacedTitle: rep.title, title: (mine && mine.title) || '',
          undo: function () { return gw.addLookItem({ productId: rep.productId, snap: rep.snap }).then(function () { return true; }, function () { return false; }); }
        });
      }, function (err) {
        var limit = err && err.fieldErrors && err.fieldErrors.items;
        return done(limit ? 'limit' : 'error', { message: limit || (err && err.message) || '', kind: (err && err.kind) || '' });
      });
    }, function (err) { return done('error', { message: (err && err.message) || '', kind: (err && err.kind) || '' }); });
  }

  // several products (a look's garments), one after the other so the limit is honoured in order
  function summarizeAdds(results) {
    var by = function (s) { return results.filter(function (r) { return r.status === s; }); };
    var first = function (list) { return list.length ? list[0] : null; };
    var limit = first(by('limit'));
    if (limit) return { status: 'limit', message: limit.message, results: results };
    var swapped = by('replaced');
    if (swapped.length) return Object.assign({ status: 'replaced', results: results, count: swapped.length }, swapped.length === 1 ? { message: swapped[0].message, replacedTitle: swapped[0].replacedTitle, title: swapped[0].title, undo: swapped[0].undo } : {});
    if (by('added').length) return { status: 'added', results: results };
    if (by('exists').length === results.length && results.length) return { status: 'exists', results: results };
    var err = first(by('error'));
    if (err) return { status: 'error', message: err.message, results: results };
    return { status: 'unavailable', results: results };
  }

  // the look to try, handed from another page to the probador: sessionStorage['nc-tryon-intent'] = { ids, title, at } (nothing in the URL).
  // readIntent(raw, now) -> { ids, title } when it is valid and younger than 10 minutes, else null (pure, for tests)
  var INTENT_KEY = 'nc-tryon-intent';
  var INTENT_TTL = 10 * 60 * 1000;
  function readIntent(raw, now) {
    var v;
    try { v = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (e) { return null; }
    if (!v || !Array.isArray(v.ids) || !v.ids.length || typeof v.at !== 'number' || now - v.at > INTENT_TTL || now < v.at - 60000) return null;
    var ids = v.ids.filter(function (id) { return typeof id === 'string' && /^[a-z0-9-]{1,80}$/i.test(id); }).slice(0, 8);
    return ids.length ? { ids: ids, title: typeof v.title === 'string' ? v.title.slice(0, 80) : '' } : null;
  }
  // N6 (Tanda 2): a garment added on another page is highlighted when the person lands on the probador. Every add notes
  // sessionStorage['nc-tryon-arrival'] = { id, path, at }; the probador's room (arrival="on") reads it once on its first look load and
  // highlights that garment's tile for ARRIVAL_SHOW_MS when it was added on ANOTHER path less than ARRIVAL_TTL ago and is in the look.
  // arrivalRecord(id, path, now) -> the JSON to store; readArrival(raw, now, path) -> { id } | null (pure, for tests)
  var ARRIVAL_KEY = 'nc-tryon-arrival';
  var ARRIVAL_TTL = 10 * 60 * 1000;
  var ARRIVAL_SHOW_MS = 2000;
  function arrivalRecord(id, path, now) { return JSON.stringify({ id: String(id || ''), path: String(path || ''), at: now }); }
  function readArrival(raw, now, path) {
    var v;
    try { v = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (e) { return null; }
    if (!v || typeof v.id !== 'string' || !/^[a-z0-9-]{1,80}$/i.test(v.id) || typeof v.at !== 'number') return null;
    if (now - v.at > ARRIVAL_TTL || now < v.at - 60000) return null;
    if (typeof v.path === 'string' && v.path === String(path || '')) return null; // added here: nothing to point at on arrival
    return { id: v.id };
  }
  // the path of this page, read from document.URL (the component never navigates)
  function pagePath() { try { return new URL(document.URL).pathname; } catch (e) { return ''; } }
  function noteArrival(id) {
    if (!id || typeof window === 'undefined') return;
    try { window.sessionStorage.setItem(ARRIVAL_KEY, arrivalRecord(id, pagePath(), Date.now())); } catch (e) { /* storage blocked: no highlight */ }
  }

  var DEFAULT_LOOK_COPY = ['Estás probando «{look}». Hemos apartado tu look anterior.', 'Estás probando «{look}».', 'Este look ya está en tu probador.', 'Hemos recuperado tu look anterior.', 'Faltan {n} prendas de este look: ya no están disponibles.', 'este look'];

  var DEFAULT_REPLACED = 'Sustituimos «{old}» por «{new}»: solo cabe una prenda de este tipo.';
  var DEFAULT_REPLACED_MANY = 'Sustituimos {n} prendas del mismo tipo: solo cabe una prenda de cada tipo.';
  var DEFAULT_UNDO = 'Deshacer';
  var DEFAULT_RESTORED = 'Hemos vuelto a poner la prenda anterior';

  // message of the toast the entry points show: copy = { added, error, limit } (host copy); the gateway's own message wins for the limit
  function toastFor(result, copy) {
    copy = copy || {};
    if (!result) return null;
    if (result.status === 'added' || result.status === 'exists') return { tone: 'success', message: copy.added || '', detail: copy.detail };
    if (result.status === 'replaced') {
      // one garment per body slot: "Sustituimos «A» por «B»: solo cabe una prenda de este tipo." (the host's copy wins, else the default)
      var many = result.count > 1;
      var text = many ? fill(copy.replacedMany || DEFAULT_REPLACED_MANY, { n: result.count })
        : result.message || fill(copy.replaced || DEFAULT_REPLACED, { old: result.replacedTitle || '', 'new': result.title || '' });
      return { tone: 'success', message: text, undo: !many && typeof result.undo === 'function' };
    }
    if (result.status === 'limit') return { tone: 'error', message: result.message || copy.limit || copy.error || '' };
    if (result.status === 'error') return { tone: 'error', message: copy.error || result.message || '' };
    return null;
  }

  // ---- chooser chips: the "Probar" / "Cambiar" / "En tu look" chip (CMP-006) of a product card, ONE code path for every host grid ----
  // (the probador chooser, the Brand Hub's PRODUCTOS panel and the Branding Corner when its room is in user mode). The chip's data-value
  // is the product id; the host keeps `catalog` (id -> { id, title, slot, ... }) and the room is the look model. Copy:
  //   { add: 'Probar', inLook: 'En tu look', change: 'Cambiar', addAria: 'Probar {name}', changeAria: 'Cambiar «{old}» por «{name}»' }
  var CHIP_COPY = { add: 'Probar', inLook: 'En tu look', change: 'Cambiar', addAria: 'Probar {name}', changeAria: 'Cambiar «{old}» por «{name}»' };

  // what a chip says for `product` against the look: pressed = in the look; a slot taken by ANOTHER garment says "Cambiar" and names it
  function chooserChipState(look, pending, product, id, copy) {
    var c = {};
    Object.keys(CHIP_COPY).forEach(function (k) { c[k] = copy && copy[k] ? copy[k] : CHIP_COPY[k]; }); // empty copy falls back to the probador's
    var inLook = (look || []).some(function (p) { return p.id === id; });
    var taken = inLook || !product ? null : slotClash(look || [], product);
    var name = product ? product.title : '';
    return {
      pressed: inLook,
      text: inLook ? c.inLook : taken ? c.change : c.add,
      label: !product ? null : taken ? fill(c.changeAria, { old: taken.title, name: name }) : fill(c.addAria, { name: name }),
      taken: taken,
      busy: !!(pending && pending[id])
    };
  }

  // paints every chip of a grid from the room's look (call it on `nc-fitting:change` and after rendering the grid)
  function syncChooserChips(chips, room, catalog, copy) {
    var look = room && room.look ? room.look : [];
    var pending = room && room.pending ? room.pending : {};
    Array.prototype.forEach.call(chips || [], function (chip) {
      var id = chip.getAttribute('data-value');
      var main = chip.querySelector('.nc-chip__main');
      if (!main) return;
      var st = chooserChipState(look, pending, (catalog || {})[id], id, copy);
      main.setAttribute('aria-pressed', st.pressed ? 'true' : 'false');
      main.textContent = st.text;
      if (st.label) main.setAttribute('aria-label', st.label);
      if (st.busy) main.setAttribute('aria-busy', 'true'); else main.removeAttribute('aria-busy');
    });
  }

  // a chip press (`nc-chip:toggle` {pressed}): adds or removes the garment through room.toggle (slot rule, replacement toast + Deshacer,
  // rollback on a gateway refusal). Resolves the room's result, or null when nothing was done (unknown product, change in flight, no room)
  function toggleChooserChip(room, catalog, chip, pressed) {
    var id = chip && chip.getAttribute('data-value');
    var product = id && catalog ? catalog[id] : null;
    if (!room || typeof room.toggle !== 'function' || !product || (room.pending && room.pending[id])) return Promise.resolve(null);
    return room.toggle(product, !!pressed);
  }

  var core = {
    parsePrice: parsePrice, formatPrice: formatPrice, normalizePrice: normalizePrice, totalPrice: totalPrice, plural: plural, fill: fill,
    inverseOp: inverseOp, historyEmpty: historyEmpty, historyPush: historyPush, historyUndo: historyUndo, historyRedo: historyRedo,
    planOps: planOps, planReplace: planReplace, readIntent: readIntent, readArrival: readArrival, arrivalRecord: arrivalRecord, ARRIVAL_SHOW_MS: ARRIVAL_SHOW_MS, rollbackOps: rollbackOps, makeEntry: makeEntry, undoOps: undoOps, redoOps: redoOps, errorCopy: errorCopy,
    collapsePlan: collapsePlan, rowDetail: rowDetail, progressPercent: progressPercent, presets: Presets,
    pickRoom: pickRoom, storeTarget: storeTarget, slotClash: slotClash, CHIP_COPY: CHIP_COPY, chooserChipState: chooserChipState, syncChooserChips: syncChooserChips, toggleChooserChip: toggleChooserChip, showQuota: showQuota, createRenderFlow: createRenderFlow, classifyRenderError: classifyRenderError, consentOk: consentOk, addToLook: addToLook, summarizeAdds: summarizeAdds, toastFor: toastFor
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  else window.NcFittingRoomCore = core;

  // ---- window.NcFittingRoom: the public API of the component for any screen (step 1.6). Safe to call without a gateway (no-op). ----
  //   add(productId, { variantId })  -> Promise<{ status: 'added' | 'replaced' | 'exists' | 'limit' | 'error' | 'unavailable', productId, message? }>
//     'replaced' (one garment per body slot): { replacedTitle, title, message?, undo() } - the garment of the same slot was swapped, undo() puts it back
  //   addMany([productId, ...])      -> the same shape for a look's garments, one after the other
  //   notify(result, { added, error, limit, replaced ('{old}'/'{new}'), undoLabel, restored, actionLabel, onAction }) -> shows the result with nc-toast (nothing for 'unavailable');
  //     actionLabel / onAction put one action button on the success toast (e.g. "Ver ficha")
  // It adds to the CURRENT look through gateway.addLookItem (the gateway refuses a garment over the limit; getTryOnConfig has the limit).
  // When a <nc-fitting-room> is on the page the change goes through its look model (same optimistic update and rollback, all views at
  // once). It emits a bubbling `nc-fitting:added` {productId, status} on document and NEVER navigates: that is the host's (Phase B).
  function currentGateway() {
    try { return window.NcApp && window.NcApp.gateway ? window.NcApp.gateway() : null; } catch (e) { return null; }
  }

  // D-PROB-2 / step 1 of the unified probador: the room that takes a garment is the first one that CAN add (ready, look loaded, not a
  // viewer), preferring one marked data-active; the first <nc-fitting-room> of the DOM may be a viewer or still loading
  function pickRoom(rooms) {
    var usable = Array.prototype.filter.call(rooms || [], function (el) {
      return el && el.ready && typeof el.addProduct === 'function' && typeof el.canAdd === 'function' && el.canAdd();
    });
    for (var i = 0; i < usable.length; i++) if (usable[i].hasAttribute && usable[i].hasAttribute('data-active')) return usable[i];
    return usable[0] || null;
  }

  function liveRoom() { return pickRoom(document.querySelectorAll('nc-fitting-room')); }

  // the page's look sheet (assets/nc-look-sheet.js) that holds `room`: a "Probar" opens it instead of navigating (D-PROB-2)
  function sheetOf(room) {
    var sheet = room && room.closest ? room.closest('nc-look-sheet') : null;
    return sheet && typeof sheet.open === 'function' ? sheet : null;
  }

  function announceAdded(result) {
    if (result.status === 'unavailable') return result;
    if (result.status === 'added' || result.status === 'replaced') noteArrival(result.productId);
    document.dispatchEvent(new CustomEvent('nc-fitting:added', { bubbles: true, detail: { productId: result.productId, status: result.status } }));
    return result;
  }

  function addOne(productId, options) {
    var room = liveRoom();
    var run = room ? room.addProduct(productId, options) : addToLook(currentGateway(), productId, options);
    var sheet = options && options.open ? sheetOf(room) : null;
    return run.then(function (r) { if (sheet) sheet.open(); return announceAdded(r); });
  }

  function addMany(ids, options) {
    var results = [];
    var chain = (ids || []).reduce(function (p, id) {
      return p.then(function () { return addOne(id, options).then(function (r) { results.push(r); }); });
    }, Promise.resolve());
    return chain.then(function () { return summarizeAdds(results); });
  }

  function notify(result, copy) {
    var t = toastFor(result, copy);
    if (t && t.message && window.NcToast) {
      var swap = t.undo;
      var withAction = !swap && t.tone === 'success' && copy && copy.actionLabel && typeof copy.onAction === 'function';
      var el = window.NcToast.show({
        tone: t.tone, message: t.message, detail: t.detail,
        actionLabel: swap ? ((copy && copy.undoLabel) || DEFAULT_UNDO) : withAction ? copy.actionLabel : undefined, actionId: swap ? 'undo' : 'toast-action'
      });
      if (el && swap) {
        el.addEventListener('nc-toast:action', function () {
          Promise.resolve(result.undo()).then(function (ok) {
            if (ok !== false) window.NcToast.show({ tone: 'success', message: (copy && copy.restored) || DEFAULT_RESTORED });
            else window.NcToast.show({ tone: 'error', message: (copy && copy.error) || '' });
          });
        });
      } else if (el && withAction) el.addEventListener('nc-toast:action', function () { copy.onAction(result); });
    }
    return t;
  }

  // "Probar este look" from any page (UX audit 2026-10-25, docs/normcore-probador-look-en-curso-ux.md): a whole look always opens the
  // probador and REPLACES the current look there (Deshacer brings the previous one back). On a page with a combining fitting room the look
  // loads in place; elsewhere the intent travels in sessionStorage and NcNav opens the try-on route. Without route or storage it falls back
  // to addMany (the old behaviour). Resolves { status: 'navigating' | 'replaced' | <addMany status> }.
  function tryLook(ids, meta) {
    ids = (ids || []).filter(Boolean);
    meta = meta || {};
    if (!ids.length) return Promise.resolve({ status: 'unavailable' });
    var room = liveRoom();
    if (room && typeof room.replaceLook === 'function') {
      // on a page with a look sheet (Brand Corner) the look loads there and the sheet opens: never a navigation (D-PROB-2)
      var sheet = sheetOf(room);
      return room.replaceLook(ids, meta).then(function (r) { if (sheet) sheet.open(); return r; });
    }
    var url = window.NcNav && window.NcNav.url ? window.NcNav.url('try_on') : '';
    if (url) {
      try {
        window.sessionStorage.setItem(INTENT_KEY, JSON.stringify({ ids: ids, title: meta.title || '', at: Date.now() }));
        window.NcNav.go('try_on');
        return Promise.resolve({ status: 'navigating' });
      } catch (e) { /* storage blocked: add in place */ }
    }
    return addMany(ids);
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.NcFittingRoom) {
    window.NcFittingRoom = { add: addOne, addMany: addMany, notify: notify, tryLook: tryLook };
  }
  if (typeof customElements === 'undefined' || customElements.get('nc-fitting-room')) return;

  function noop() {}

  // indexes of data-msg-messages
  // PHOTO-WAIT-BEGIN
  // The photo check is promised at ~8 s; the real validator can take 35 s. After SLOW_MS the copy says it is slow, after ESCAPE_MS the user may continue
  // without the answer (the backend treats a skipped validation as ok and says so at create time). Timers are injectable (tests) and stop() clears both.
  var PHOTO_SLOW_MS = 10000;
  var PHOTO_ESCAPE_MS = 20000;
  function makePhotoWait(setT, clearT, onSlow, onEscape) {
    var slow = null;
    var escape = null;
    function stop() {
      if (slow !== null) clearT(slow);
      if (escape !== null) clearT(escape);
      slow = null;
      escape = null;
    }
    return {
      start: function () {
        stop();
        slow = setT(function () { slow = null; onSlow(); }, PHOTO_SLOW_MS);
        escape = setT(function () { escape = null; onEscape(); }, PHOTO_ESCAPE_MS);
      },
      stop: stop,
      pending: function () { return slow !== null || escape !== null; }
    };
  }
  // PHOTO-WAIT-END

  // Shopify CDN images take their width from `width=`: the same file at 240 / 480 / 720 px for the browser to choose from (F11)
  var SRCSET_WIDTHS = [240, 480, 720];
  function srcsetFor(src) {
    var s = String(src || '');
    if (!/[?&]width=\d+/.test(s) || s.indexOf('/cdn/shop/') < 0) return '';
    return SRCSET_WIDTHS.map(function (w) { return s.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w'; }).join(', ');
  }

  var MSG = { ADD_ERROR: 0, REMOVE_ERROR: 1, FAVORITE_ERROR: 2, LOOK_ERROR: 3, SAVE_FIRST: 4, UNUSED5: 5, TOOL: 6, MODEL: 7, SHARE_ERROR: 8, SAVE_ERROR: 9, ADDED: 10, REMOVED: 11, EMPTY: 12, CONNECT: 13, CLEARED: 14, RESTORED: 15 };
  var MSG_DEFAULTS = { 14: 'Look vaciado', 15: 'Hemos recuperado tu look' };
  // the ONE photo dialog (data-msg-photo): title / button without a photo | with a photo | camera action | drop copy on touch
  var PHOTO_DEFAULTS = ['Tu foto', 'Cambiar foto', 'Hacer una foto', 'Elige una foto o hazla ahora', 'Usar y crear', 'Más información'];
  // A2 example placeholder (data-msg-example) and the "Ver mi look" label of the create button (data-msg-create)
  var EXAMPLE_DEFAULTS = ['Así se verá tu look', 'Elige prendas y pulsa Crear mi look: te lo enseñamos puesto.', 'Ejemplo', 'Mis looks ({n})', 'Toca un look para verlo en grande.', 'Ver mis looks'];
  // below this width of the component the catalogue is a carousel under the canvas and GENERAR rides a sticky bottom bar
  var NARROW = 700;
  // looks mode (§4.2): from this width of the component the desktop has two columns, builder (slot row + catalogue + CREAR) | stage,
  // and "Mi look actual" is not shown (the slot row is the look). 1000 px of the component = a 1064 px window with the theme gutters (1081 px
  // with a classic scrollbar), so a 1100 px laptop gets the two columns (N2 of docs/normcore-probador-revalidacion.md); the same threshold
  // is INLINE in assets/nc-looks.js and the looks block of assets/nc-fitting-room.css
  var WIDE = 1000;
  // the slot row (data-msg-slot-row): title | count ('{n}', '{max}') | empty slot aria ('{slot}') | add tile | line of the empty row (N3)
  var SLOT_ROW_DEFAULTS = ['Tu look', '({n}/{max})', 'Elegir {slot}', 'Añadir prenda', 'elige prendas abajo', 'Acabas de añadir «{name}» a tu look'];

  class NcFittingRoomElement extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      var self = this;
      var attr = function (n) { var v = self.getAttribute(n); return v === null ? undefined : v; };
      this.cfg = Presets.resolve(attr('preset'), {
        mode: attr('mode'), modelSource: attr('model-source'), tools: attr('tools'), actions: attr('actions'), itemFields: attr('item-fields'),
        views: attr('views'), combine: attr('combine'), chooserPosition: attr('chooser-position'), head: attr('head'), addPosition: attr('add-position'), lookLimit: attr('look-limit')
      });
      this.gw = window.NcApp.gateway();
      this.look = [];
      this.viewer = this.cfg.mode === 'brand' && this.cfg.combine === 'off';
      this.brand = this.getAttribute('brand') || '';
      this.catalog = {};
      this.pending = {};
      this.history = historyEmpty();
      this.savedLookId = null;
      this.saving = false;
      this.lookError = false;
      this.expanded = false;
      this.modelId = null;
      this.modelPhoto = false;
      this.reqLook = 0;
      this.rendering = false;
      this.lastRender = null;
      this.renderNote = null;
      this.prevRender = null;
      this.renderFailed = false;
      this.uploadOpen = false;
      this.avatarUploadId = this.getAttribute('avatar-upload-id') || null;
      this.looked = false;
      var pollAttr = parseInt(this.getAttribute('data-poll-ms') || '', 10);
      var rfOpts = pollAttr >= 0 ? { pollMs: pollAttr } : {};
      // A5: nobody waits in front of the render any more, so it may take its real time (§8: ~20 s, more on a busy day) before it times out
      if (this.getAttribute('looks') === 'on') rfOpts.maxPolls = 150;
      this.rf = createRenderFlow(this.gw, this.cfg, rfOpts);
      this.looksOn = this.getAttribute('looks') === 'on' && this.rf.enabled;
      this.jobs = {};
      this.jobSeq = 0;
      this.m = {
        look: this.list('data-msg-look'), counts: this.list('data-msg-counts'), tryon: this.list('data-msg-tryon'), save: this.list('data-msg-save'),
        share: this.list('data-msg-share'), messages: this.list('data-msg-messages'), errors: this.list('data-msg-errors'), model: this.list('data-msg-model'),
        more: this.list('data-msg-more'), fields: this.list('data-msg-fields'), render: this.list('data-msg-render'), consent: this.list('data-msg-consent'),
        renderErrors: this.list('data-msg-render-errors'), retain: this.list('data-msg-retain'), states: this.list('data-msg-states'), slots: this.list('data-msg-slots'), lookCopy: this.list('data-msg-look-copy'), demo: this.list('data-msg-demo'), photo: this.list('data-msg-photo'),
        example: this.list('data-msg-example'), create: this.list('data-msg-create'), slotRow: this.list('data-msg-slot-row')
      };
      this.stageEl = this.querySelector('.nc-fr__stage');
      this.lookEl = this.querySelector('nc-current-look');
      this.tryon = this.querySelector('nc-tryon');
      this.statusEl = this.querySelector('[data-fr-status]');
      this.sheet = this.querySelector('nc-sheet');
      this.setupConfig();
      this.setupExtras();
      this.setupRender();
      if (this.looksOn) this.setupLooks();

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('nc-chip:toggle', this.onPreset.bind(this));
      this.addEventListener('nc-look:remove', this.onLookRemove.bind(this));
      this.addEventListener('nc-look:save', this.onMain.bind(this));
      this.addEventListener('nc-look:favorite', this.onFavorite.bind(this));
      this.addEventListener('nc-look:retry', function () { self.loadLook(); });
      this.addEventListener('click', function (e) {
        var link = e.target.closest && e.target.closest('[data-fr-store-link] a');
        if (!link) return;
        var here; // read from document.URL, the component itself never touches the location
        try { here = new URL(document.URL); } catch (err) { return; }
        if (storeTarget(here.pathname, here.search) !== 'here') return;
        e.preventDefault();
        var sheet = self.closest('nc-look-sheet');
        if (sheet && typeof sheet.close === 'function') sheet.close();
        var products = document.querySelector('[data-bc-chooser-products-section]');
        if (products && products.scrollIntoView) products.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      this.addEventListener('nc-tryon:retry', function () { if (self.renderFailed) self.generate(); else self.loadLook(); });
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('dragenter', this.onDrag.bind(this));
      this.addEventListener('dragover', this.onDrag.bind(this));
      this.addEventListener('dragleave', this.onDrag.bind(this));
      this.addEventListener('drop', this.onDrag.bind(this));
      this.addEventListener('nc-tryon:tool', this.onViewTool.bind(this));
      this.addEventListener('nc-sheet:action', this.onSheetAction.bind(this));
      this.addEventListener('nc-sheet:action', this.onConsentAction.bind(this));
      this.addEventListener('nc-sheet:close', this.onSheetClose.bind(this));
      this.addEventListener('keydown', this.onToolKeydown.bind(this));
      this.addEventListener('keydown', this.onPresetKeydown.bind(this));
      Promise.all(['nc-current-look', 'nc-tryon'].map(function (n) { return customElements.whenDefined(n); })).then(function () {
        self.loadLook();
        self.loadQuota();
        self.initModels();
      });
    }

    list(attr) { return (this.getAttribute(attr) || '').split('|').map(function (x) { return x.trim(); }); }

    q(sel) { return this.querySelector(sel); }

    msg(i) { return this.m.messages[i] || MSG_DEFAULTS[i] || ''; }

    photoCopy(i) { return this.m.photo[i] || PHOTO_DEFAULTS[i]; }

    // touch / phone: the photo dialog offers the camera and says "Elige una foto o hazla ahora" instead of the drag-and-drop copy
    isTouch() {
      var mq = window.matchMedia ? window.matchMedia('(hover: none) and (pointer: coarse)') : null;
      return !!(mq && mq.matches) || window.innerWidth < NARROW;
    }

    hasPhoto() { return !!(this.avatarUploadId || this.avatarUrl); }

    // the model button (desktop) and the person icon (tablet, mobile) say "Tu foto" until there is one, then "Cambiar foto"
    syncPhotoLabels() {
      if (this.cfg.modelPresets || this.cfg.modelSource !== 'upload') return;
      var text = this.photoCopy(this.hasPhoto() ? 1 : 0);
      var label = this.q('[data-fr-model-open] span');
      if (label) label.textContent = text;
      var icon = this.q('[data-fr-model-sheet]');
      if (icon) { icon.setAttribute('aria-label', text); icon.removeAttribute('aria-haspopup'); icon.removeAttribute('aria-expanded'); }
    }

    announce(text) { if (this.statusEl) this.statusEl.textContent = text; }

    toast(message, tone) { if (window.NcToast) window.NcToast.show({ tone: tone || 'error', message: message }); }

    has(group, id) { return this.cfg[group].indexOf(id) >= 0; }

    emit(name, detail, cancelable) {
      return this.dispatchEvent(new CustomEvent('nc-fitting:' + name, { bubbles: true, cancelable: !!cancelable, detail: detail || {} }));
    }

    // images enter only through the gateway's asset records; no src (placeholder) leaves the neutral slot
    applyAsset(slot, assetId, alt, eager) {
      if (!slot || !assetId || !this.gw.getAsset) return;
      this.gw.getAsset(assetId).then(function (a) {
        if (!a || !a.src || !slot.isConnected) return;
        var img = document.createElement('img');
        img.src = a.src;
        // F11: the slots are thumbnails (a 136 px card, a 62 px chip) but the catalogue prints 1200 px images: let the browser pick the width
        var set = srcsetFor(a.src);
        if (set) { img.srcset = set; img.sizes = Math.max(slot.clientWidth || 0, 96) + 'px'; }
        if (a.width) img.width = a.width;
        if (a.height) img.height = a.height;
        img.alt = a.alt || alt || '';
        img.loading = eager ? 'eager' : 'lazy';
        slot.insertBefore(img, slot.firstChild);
      }, noop);
    }

    // ---- configuration: tools, views, actions, model selector, head ----
    setupConfig() {
      var self = this;
      var box = this.q('.nc-fr__box');
      this.setAttribute('data-mode', this.cfg.mode);
      if (this.viewer) this.setAttribute('data-viewer', '');
      this.setAttribute('data-preset', this.cfg.preset);
      if (box) box.setAttribute('data-model-source', this.cfg.modelSource);
      var head = this.q('.nc-fr__head');
      if (head) head.setAttribute('data-head', this.cfg.head);
      // the DOM order of the chooser (before / after) is rendered by the snippet: it must match cfg.chooserPosition (tested)
      // side tools
      Array.prototype.forEach.call(this.querySelectorAll('[data-fr-tool]'), function (btn) { btn.hidden = !self.has('tools', btn.getAttribute('data-fr-tool')); });
      Array.prototype.forEach.call(this.querySelectorAll('[data-fr-tools]'), function (group) { group.hidden = !group.querySelector('[data-fr-tool]:not([hidden])'); });
      // canvas views (nc-tryon toolbar): only the configured ones stay
      var bar = this.q('.nc-tryon__tools');
      if (bar) {
        Array.prototype.forEach.call(bar.querySelectorAll('.nc-icon-button'), function (b) { if (!self.has('views', b.getAttribute('data-id'))) b.remove(); });
        var left = bar.querySelectorAll('.nc-icon-button');
        if (!left.length) bar.remove();
        else Array.prototype.forEach.call(left, function (b, n) { b.setAttribute('tabindex', n === 0 ? '0' : '-1'); });
      }
      // actions
      var share = this.q('[data-fr-share]');
      if (share) share.hidden = !this.has('actions', 'share');
      this.addEl = this.q('[data-fr-add]');
      if (this.addEl) this.addEl.hidden = !this.has('actions', 'add');
      // model selector: upload = button + sheet; presets = chips + sheet
      var model = this.q('[data-fr-model]');
      if (model) model.hidden = this.cfg.modelSource !== 'upload';
      var presets = this.q('[data-fr-presets]');
      if (presets) presets.hidden = this.cfg.modelSource !== 'presets';
      var trigger = this.q('[data-fr-model-sheet]');
      if (trigger) trigger.hidden = this.cfg.modelSource === 'none';
      this.buildSheet();
      this.syncPhotoLabels();
    }

    // Limpiar (and Comparar when enabled) act on the look, so they sit beside the "Mi look actual" title (UX audit 2026-10-24), not over the
    // canvas: the same buttons (44 px targets), icon-only there, the visible label kept as their accessible name and tooltip
    placeHistory() {
      var group = this.q('[data-fr-tools="history"]');
      var header = this.lookEl && this.lookEl.querySelector('.nc-current-look__header');
      if (!group || !header || group.hidden) return;
      group.classList.add('nc-fr__tools--look');
      group.setAttribute('aria-orientation', 'horizontal');
      Array.prototype.forEach.call(group.querySelectorAll('[data-fr-tool]'), function (btn) {
        var text = btn.querySelector('span');
        if (text) { btn.setAttribute('title', text.textContent.trim()); text.classList.add('nc-visually-hidden'); }
      });
      header.appendChild(group);
    }

    // the model panel (state 5): a 2 x 2 grid of the models of the selector (the hidden preset chips are the one list: circle + name, the
    // active one with a violet border), "Cerrar" and, in user mode, "Usar mi propia foto" (the way to the upload state on tablet and mobile)
    buildSheet() {
      var host = this.q('[data-fr-sheet] [data-fr-models]');
      if (!host || this.cfg.modelSource === 'none' || !this.cfg.modelPresets) return;
      var details = this.list('data-model-details');
      host.textContent = '';
      Array.prototype.forEach.call(this.querySelectorAll('[data-fr-presets] nc-chip'), function (c, n) {
        var name = c.querySelector('.nc-chip__main').textContent.trim();
        var tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'nc-fr__model-tile';
        tile.setAttribute('data-model', c.getAttribute('data-value'));
        tile.setAttribute('aria-pressed', 'false');
        var circle = document.createElement('span');
        circle.className = 'nc-fr__model-circle';
        circle.setAttribute('aria-hidden', 'true');
        circle.innerHTML = '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" focusable="false"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>';
        var label = document.createElement('span');
        label.className = 'nc-fr__model-name';
        label.textContent = name;
        tile.appendChild(circle);
        tile.appendChild(label);
        if (details[n]) {
          var sub = document.createElement('span');
          sub.className = 'nc-fr__model-sub';
          sub.textContent = details[n];
          tile.appendChild(sub);
        }
        host.appendChild(tile);
      });
      var own = this.q('[data-fr-own]');
      if (own) own.hidden = !(this.cfg.mode === 'user' && this.cfg.modelSource === 'upload' && this.rf.enabled);
    }

    // total row, "Ver más", add button and note go into the look panel in their place
    setupExtras() {
      var save = this.lookEl.querySelector('.nc-current-look__save');
      var items = this.lookEl.querySelector('.nc-current-look__items');
      var row = document.createElement('div');
      row.className = 'nc-fr__total';
      row.hidden = true;
      row.innerHTML = '<span class="nc-fr__total-label"></span><strong class="nc-fr__total-value"></strong>';
      save.parentNode.insertBefore(row, save);
      this.totalEl = row;
      var more = this.q('[data-fr-more]');
      if (more) {
        items.parentNode.insertBefore(more, items.nextSibling);
        items.id = items.id || (this.id || 'fr') + '-items';
        more.setAttribute('aria-controls', items.id);
      }
      // "Añadir prendas" sits before the main button when the preset says so (Figma 13:4717), else after the panel (Figma 11:3252)
      if (this.addEl && this.cfg.addPosition === 'before-primary') save.parentNode.insertBefore(this.addEl, save);
      // looks mode: CREAR MI LOOK is the main action of the look panel from 700 px (below, the sticky bar), right before "Guardar look"
      var create = this.q('[data-fr-create-slot]');
      if (create) save.parentNode.insertBefore(create, save);
      this.noteEl = this.q('[data-fr-note]');
      this.placeHistory();
    }

    // ---- model selector (same function at every width; below 700 px the trigger is an icon that opens the bottom sheet) ----
    initModels() {
      var self = this;
      var chips = this.querySelectorAll('[data-fr-presets] nc-chip');
      if (!chips.length) return;
      var assets = this.list('data-model-assets');
      var pressed = this.q('[data-fr-presets] .nc-chip__main[aria-pressed="true"]');
      var first = pressed ? pressed.closest('nc-chip') : chips[0];
      this.modelAssets = {};
      Array.prototype.forEach.call(chips, function (c, n) { if (assets[n]) self.modelAssets[c.getAttribute('data-value')] = assets[n]; });
      Array.prototype.forEach.call(this.querySelectorAll('[data-fr-sheet] .nc-fr__model-tile'), function (tile) {
        var circle = tile.querySelector('.nc-fr__model-circle');
        var assetId = self.modelAssets[tile.getAttribute('data-model')];
        if (circle && assetId) { circle.textContent = ''; self.applyAsset(circle, assetId, '', false); }
      });
      // upload source: the user's own photo is the model until a tile is chosen, so nothing is pre-selected; presets source selects the first
      if (this.cfg.modelSource === 'upload') return;
      this.selectModel(first.getAttribute('data-value'), false);
    }

    selectModel(id, notify) {
      var self = this;
      this.modelId = id;
      Array.prototype.forEach.call(this.querySelectorAll('[data-fr-presets] nc-chip'), function (c) {
        c.querySelector('.nc-chip__main').setAttribute('aria-pressed', c.getAttribute('data-value') === id ? 'true' : 'false');
      });
      var chip = this.q('[data-fr-presets] nc-chip[data-value="' + id + '"]');
      var name = chip ? chip.querySelector('.nc-chip__main').textContent.trim() : '';
      var trigger = this.q('[data-fr-model-sheet]');
      if (trigger && name) trigger.setAttribute('aria-label', fill(this.m.model[3] || '{name}', { name: name }));
      Array.prototype.forEach.call(this.querySelectorAll('[data-fr-sheet] .nc-fr__model-tile'), function (a) {
        var on = a.getAttribute('data-model') === id;
        a.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
      });
      // the brand's OWN photo of the model (decision (a)): an asset of the gateway, never generated; no src = the neutral canvas
      this.modelPhoto = false;
      var assetId = this.modelAssets && this.modelAssets[id];
      if (assetId) this.showModelPhoto(assetId, name);
      if (this.viewer) this.loadLook();
      if (notify) this.emit('model', { modelId: id });
    }

    showModelPhoto(assetId, alt) {
      var self = this;
      var id = this.modelId;
      if (!assetId || !this.gw.getAsset || !this.tryon) return;
      this.gw.getAsset(assetId).then(function (a) {
        if (!a || !a.src || self.modelId !== id) return;
        var fig = self.tryon.querySelector('.nc-tryon__figure');
        var img = self.tryon.querySelector('.nc-tryon__image');
        if (!img) { img = document.createElement('img'); img.className = 'nc-tryon__image'; img.width = 220; img.height = 330; fig.insertBefore(img, fig.firstChild); }
        img.src = a.src;
        img.alt = a.alt || alt || '';
        self.modelPhoto = true;
        self.tryon.setStatus('rendered');
      }, noop);
    }

    onPreset(event) {
      var chip = event.target.closest('nc-chip');
      if (!chip || !chip.closest('[data-fr-presets]')) return;
      this.selectModel(chip.getAttribute('data-value'), true);
    }

    // the upload model button (desktop) and the panel's "Usar mi propia foto" are the same function: the photo upload state of the canvas.
    // The host may cancel `nc-fitting:model` {action:'upload'} to show its own picker.
    changeModel(trigger) {
      var ok = this.emit('model', { action: 'upload' }, true);
      if (!ok) return;
      if (this.uploadEl) this.openUpload('model', trigger); else this.toast(this.msg(MSG.MODEL));
    }

    onSheetAction(event) {
      var id = event.detail && event.detail.id;
      if (!id || !event.target.closest('[data-fr-sheet]')) return;
      event.stopPropagation();
      if (id === 'upload') this.changeModel(this.q('[data-fr-model-sheet]'));
    }

    // closing the model panel returns the focus to its trigger (nc-sheet does it) and makes sure the active tile is the current model
    onSheetClose(event) {
      if (!event.target.closest('[data-fr-sheet]')) return;
      event.stopPropagation();
    }

    onModelTile(tile) {
      this.selectModel(tile.getAttribute('data-model'), true);
    }

    // ---- try-on quota and render (user mode only; brand mode never touches render, quota or consent: decision (a)) ----
    loadQuota() {
      var self = this;
      if (!this.rf.enabled) return;
      this.rf.load().then(function (r) {
        if (r.status === 'ok') {
          self.tryonConfig = r.config;
          self.emit('quota', { left: r.left, max: r.max, daily: self.rf.quota });
        }
        self.syncRender();
        self.loadSavedAvatar();
      });
    }

    // B1 / DP-2 (opt-in saved photo, behind the theme setting): a photo the person chose to keep comes back as the current one, so the photo step is skipped
    rx(i) { return (this.m.retain || [])[i] || ''; }
    loadSavedAvatar() {
      var self = this;
      if (this.getAttribute('avatar-retention') !== 'on' || this.avatarUploadId || this.avatarUrl || !this.gw || typeof this.gw.getSavedAvatar !== 'function') return;
      this.gw.getSavedAvatar().then(function (r) {
        var a = r && r.avatar;
        if (!a || !a.id || self.avatarUploadId) return;
        self.avatarUploadId = a.id;
        self.avatarUrl = null;
        self.avatarSaved = true;
        self.savedAvatarSrc = a.imageSrc || '';
        self.paintCurrentPhoto();
        self.syncPhotoLabels();
        self.syncRender();
      }, noop);
    }

    rt(i) { return this.m.render[i] || ''; }

    // F3: the validator's first reason says what to change (codes of apps/tryon/.../validate.js); any other answer keeps the generic hint
    photoReasonText(v) {
      var idx = { photo_not_full_body: 36, photo_not_one_person: 37, photo_minor: 38, photo_face_hidden: 39 };
      var reasons = (v && v.reasons) || [];
      for (var n = 0; n < reasons.length; n++) if (idx[reasons[n]]) return this.stx(idx[reasons[n]]);
      return '';
    }

    // F1: a failure shows our sentence for its kind, never the raw text of the error ("Failed to fetch", a server message)
    errText(r) {
      var kind = r && r.kind;
      return kind === 'network' || !kind ? this.rt(12) : (this.stx(35) || this.rt(12));
    }
    // B6: the designed message of a failed render by the backend's errorCode (provider_busy, timeout, photo_invalid, garment_unsupported); '' = use the generic one
    failText(code) { var key = String(code || '') + '='; var hit = (this.m.renderErrors || []).filter(function (x) { return x.indexOf(key) === 0; })[0]; return hit ? hit.slice(key.length) : ''; }

    // Bloque 3 copy (data-msg-states, see the snippet for the index list)
    stx(i) { return this.m.states[i] || ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'Tu foto actual', 'Quitar foto', 'Foto quitada', 'No se pudo quitar la foto. Inténtalo de nuevo.', 'Inicia sesión para subir tu foto.', 'Está tardando más de lo normal… seguimos comprobando', 'Puedes continuar; si la foto no sirve te lo diremos al crear el look', 'No pudimos subir tu foto.', 'Algo ha fallado de nuestro lado. Inténtalo de nuevo en un momento.', 'Se tiene que ver tu cuerpo entero, de la cabeza a los pies.', 'En la foto tiene que salir solo una persona.', 'Esta foto no se puede usar en el probador. Prueba con otra.', 'Se tiene que ver tu cara. Prueba sin gafas de sol ni gorra.', 'No pudimos leer esa imagen. Prueba con otro archivo.'][i] || ''; }

    // the render block exists in user mode only; brand mode removes it (and the consent sheet) from the DOM
    setupRender() {
      this.renderEl = this.q('[data-fr-render]');
      this.consentWrap = this.q('[data-fr-consent]');
      this.uploadEl = this.q('[data-fr-upload]');
      this.uploadWrap = this.q('[data-fr-upload-sheet]');
      if (!this.rf.enabled) {
        // brand mode: no render, quota, consent or photo upload (decision (a)): the whole block and every state overlay leave the DOM
        [this.renderEl, this.consentWrap, this.uploadWrap].forEach(function (el) { if (el) el.remove(); });
        var dock = this.q('[data-fr-dock]');
        if (dock) dock.remove();
        Array.prototype.forEach.call(this.querySelectorAll('[data-fr-overlay]'), function (el) { el.remove(); });
        this.renderEl = null;
        this.consentWrap = null;
        this.uploadWrap = null;
        this.uploadEl = null;
        return;
      }
      this.renderEl.hidden = false;
      this.renderBtn = this.renderEl.querySelector('.nc-button');
      this.hintEl = this.renderEl.querySelector('[data-fr-render-hint]');
      this.badgeEl = this.renderEl.querySelector('[data-fr-ai-badge]');
      this.aiNoteEl = this.renderEl.querySelector('[data-fr-ai-note]');
      this.aiCanvasEl = this.tryon ? this.tryon.querySelector('[data-tryon-ai]') : null;
      this.consentSheet = this.consentWrap && this.consentWrap.querySelector('nc-sheet');
      this.renderBtn.addEventListener('click', this.onGenerate.bind(this));
      // GENERAR in the canvas footer (PV-14); the hint and the helper line stay under the canvas
      var slot = this.looksOn ? this.q('[data-fr-create-slot]') : this.tryon && this.tryon.querySelector('[data-tryon-slot]');
      var action = this.renderEl.querySelector('[data-fr-generate]');
      if (slot && action) { slot.appendChild(action); slot.hidden = false; }
      this.setupDock(slot, action);
      this.consentCheck = this.consentWrap && this.consentWrap.querySelector('[data-fr-consent-check]');
      this.consentAccept = this.consentWrap && this.consentWrap.querySelector('.nc-sheet__action[data-action="accept"]');
      if (this.consentAccept) this.consentAccept.disabled = true;
      this.fileInput = this.q('[data-fr-file]:not([data-fr-camera])');
      this.cameraInput = this.q('[data-fr-camera]');
      this.useBtn = this.q('[data-fr-act="use"] .nc-button');
      this.uploadSheet = this.uploadWrap && this.uploadWrap.querySelector('nc-sheet');
      this.addEventListener('nc-sheet:close', this.onUploadSheetClose.bind(this));
      // B3: the preset models of the photo step ("Elige un modelo"), a radiogroup above the drop area
      this.pmEl = this.q('[data-fr-pm]');
      this.pmTiles = this.pmEl ? Array.prototype.slice.call(this.pmEl.querySelectorAll('.nc-fr__pm-tile')) : [];
      this.pmCopy = this.pmEl ? (this.pmEl.getAttribute('data-msg-pm') || '').split('|').map(function (x) { return x.trim(); }) : [];
      this.pmArmed = null;
      this.pmAutoUse = null;
      this.avatarModelId = null;
    }

    // ---- B3 (DP-1 b): "Elige un modelo" in the photo dialog. A model is uploaded as the user's photo by the SAME path as an own file ----
    pmx(i) { return (this.pmCopy && this.pmCopy[i]) || ''; }

    pmTile(id) { return this.pmTiles.filter(function (t) { return t.getAttribute('data-fr-pm-id') === id; })[0] || null; }

    // one checked tile at most; roving tabindex: the checked tile (else the first) is the one stop of the group in the Tab order
    markPresetModel(id) {
      var tiles = this.pmTiles || [];
      var on = id ? this.pmTile(id) : null;
      tiles.forEach(function (t, n) {
        var checked = t === on;
        t.setAttribute('aria-checked', checked ? 'true' : 'false');
        t.tabIndex = checked || (!on && n === 0) ? 0 : -1;
      });
    }

    // the dialog opens: preselect (never upload) the tile pickPresetModel says; "Usar" is armed with it when there is no photo yet
    preparePresetModels() {
      var Models = presetModels();
      if (!this.pmTiles || !this.pmTiles.length || !Models) return null;
      var pick = Models.pick(this.pmTiles.map(function (t) { return t.getAttribute('data-fr-pm-id'); }), {
        hasPhoto: this.hasPhoto(), current: this.avatarModelId, remembered: Models.readRemembered(), reason: this.uploadReason
      });
      window.clearTimeout(this.pmTimer);
      this.pmAutoUse = null;
      this.pmArmed = pick.armed ? pick.id : null;
      this.markPresetModel(pick.id);
      return pick.id ? this.pmTile(pick.id) : null;
    }

    // a tile was chosen (click, Enter / Space, or an arrow key after a short pause): its upload starts, exactly like picking a file
    choosePresetModel(id, delay) {
      var self = this;
      if (!id || !this.uploadOpen) return;
      var tile = this.pmTile(id);
      if (!tile) return;
      var same = tile.getAttribute('aria-checked') === 'true' && (this.pendingUpload && this.pendingUpload.modelId === id || this.pmBusy === id);
      this.markPresetModel(id);
      this.pmArmed = null;
      window.clearTimeout(this.pmTimer);
      if (same) return;
      if (delay) { this.resetUpload(); this.pmTimer = window.setTimeout(function () { self.startPresetUpload(id, false); }, delay); return; }
      this.startPresetUpload(id, false);
    }

    // fetch the asset (same origin) as a File and hand it to onFile; autoUse = "Usar" was pressed on a preselected model: carry on once valid
    startPresetUpload(id, autoUse) {
      var self = this;
      var tile = this.pmTile(id);
      if (!tile || !this.uploadOpen) return;
      var src = tile.getAttribute('data-fr-pm-src');
      var name = tile.getAttribute('data-fr-pm-name') || '';
      this.resetUpload();
      var seq = this.uploadSeq;
      this.pmBusy = id;
      this.setDrop('busy', this.stx(23), name);
      this.announce(this.stx(23));
      var fail = function () {
        if (seq !== self.uploadSeq || !self.uploadOpen) return;
        self.pmBusy = null;
        self.showBadPhoto(self.pmx(4) || self.stx(19), '');
      };
      var Models = presetModels();
      if (!Models) { fail(); return; }
      Models.loadFile(src, id).then(function (file) {
        if (seq !== self.uploadSeq || !self.uploadOpen) return;
        self.pmBusy = null;
        self.onFile(file, { modelId: id, label: name });
        if (autoUse) self.pmAutoUse = self.uploadSeq;
        if (self.pmx(5)) self.announce(fill(self.pmx(5), { name: name }));
      }, fail);
    }

    // "Usar" pressed on a preselected model (nothing uploaded yet): upload it now and carry on as soon as it is valid
    maybeAutoUse(seq) {
      if (this.pmAutoUse == null || this.pmAutoUse !== seq || !this.pendingUpload || !this.useBtn || this.useBtn.disabled) return false;
      this.pmAutoUse = null;
      this.useUploadedPhoto();
      return true;
    }

    onPresetKeydown(event) {
      var tile = event.target.closest && event.target.closest('.nc-fr__pm-tile');
      if (!tile || !this.pmTiles.length) return;
      var Models = presetModels();
      var next = Models ? Models.step(this.pmTiles.indexOf(tile), event.key, this.pmTiles.length) : -1;
      if (next < 0) return;
      event.preventDefault();
      var to = this.pmTiles[next];
      to.focus();
      // arrows move the selection too (radio pattern); the upload waits a moment so running through the tiles does not upload each one
      this.choosePresetModel(to.getAttribute('data-fr-pm-id'), 350);
    }

    // below 700 px of the component (UX audit, F) GENERAR leaves the canvas footer for a sticky bar at the bottom of the screen with the
    // look count, so it is one tap away while the person picks garments in the carousel under the canvas. The SAME button moves (one
    // state, one focus target); from 700 px it goes back to the canvas footer.
    setupDock(slot, action) {
      var dock = this.q('[data-fr-dock]');
      var box = this.q('.nc-fr__box');
      if (!dock || !slot || !action || !box) return;
      this.dockEl = dock;
      var self = this;
      // looks mode, desktop (>= 1100 px): CREAR and its quota line ride the sticky bar at the bottom of the builder column (§4.2)
      var bar = this.looksOn ? this.q('[data-fr-builder-create]') : null;
      var hint = this.hintEl;
      var place = function () {
        var w = box.getBoundingClientRect().width;
        var narrow = w < NARROW;
        var wide = !!bar && w >= WIDE;
        dock.hidden = !narrow || (self.looksOn && !self.look.length);
        var target = narrow ? dock : wide ? bar : slot;
        if (action.parentNode !== target) {
          var focused = action.contains(document.activeElement);
          target.appendChild(action);
          if (focused && self.renderBtn) self.renderBtn.focus({ preventScroll: true });
        }
        if (hint && self.renderEl) {
          if (wide && hint.parentNode !== bar) bar.appendChild(hint);
          else if (!wide && hint.parentNode !== self.renderEl) self.renderEl.insertBefore(hint, self.renderEl.firstChild);
        }
        slot.hidden = narrow || wide;
        if (self.looksOn) { self.toggleAttribute('data-fr-wide', wide); self.orderBuilder(wide); }
      };
      place();
      this.placeDock = place;
      if (typeof ResizeObserver === 'function') new ResizeObserver(place).observe(box);
      else window.addEventListener('resize', place);
    }

    // N7 of the re-validation: the DOM follows the reading order at every width. On the two-column desktop the builder comes before the
    // stage, so Tab runs builder → stage; below it the server order stays (the compact stage first on mobile). The builder holds the host's
    // chooser, so the stage section is the one that moves (a comment marks its server place); the focus stays where it was.
    orderBuilder(wide) {
      var layout = this.q('[data-fr-layout]');
      var builder = this.q('[data-fr-builder]');
      var canvas = this.q('.nc-fr__canvas');
      if (!layout || !builder || !canvas || builder.parentNode !== layout || canvas.parentNode !== layout) return;
      if (!this.canvasMark) { this.canvasMark = document.createComment('nc-fr stage'); layout.insertBefore(this.canvasMark, canvas); }
      var target = wide ? builder.nextSibling : this.canvasMark.nextSibling;
      if (target === canvas || (wide && canvas.previousElementSibling === builder)) return;
      if (!wide && this.canvasMark.nextSibling === canvas) return;
      var focused = canvas.contains(document.activeElement) ? document.activeElement : null;
      layout.insertBefore(canvas, target);
      if (focused && focused.isConnected) focused.focus({ preventScroll: true });
    }

    // below 700 px GENERAR is pressed from the sticky bar, usually with the canvas scrolled away: the result is brought into view
    revealStage() {
      if (!this.dockEl || this.dockEl.hidden || !this.stageEl) return;
      var r = this.stageEl.getBoundingClientRect();
      if (r.top >= 0 && r.bottom <= window.innerHeight) return;
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.stageEl.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
    }

    // the avatar photo comes from the host (the picker is Phase B): its upload id
    setAvatar(uploadId) {
      this.avatarUploadId = uploadId || null;
      this.syncRender();
    }

    setHint(text, tone) {
      if (!this.hintEl) return;
      this.hintEl.textContent = text || '';
      if (tone) this.hintEl.setAttribute('data-tone', tone); else this.hintEl.removeAttribute('data-tone');
    }

    // button + hint follow the model: look, quota, session, last outcome. Disabled = out of tab order, with the reason written next to it.
    syncRender() {
      this.syncRenderButton();
      this.syncStage();
    }

    syncRenderButton() {
      if (!this.rf || !this.rf.enabled || !this.renderEl) return;
      if (this.looksOn) { this.syncCreateButton(); return; }
      var btn = this.renderBtn;
      var n = this.look.length;
      var left = this.rf.left;
      var max = this.rf.max;
      if (this.rendering) { btn.setAttribute('data-state', 'loading'); return; }
      if (btn.getAttribute('data-state') === 'loading') btn.setAttribute('data-state', 'default');
      var hint = { text: '', tone: '' };
      var off = false;
      if (this.lastRender && this.lastRender.assetId && !this.renderNote) hint = { text: this.isDemo(this.lastRender) ? this.demoCopy(1) : this.rt(9), tone: 'ok' };
      if (showQuota(left) && left > 0 && !this.renderNote) hint = hint.text ? hint : { text: fill(plural(this.rt(3), left), { n: left }), tone: '' };
      if (this.avatarSaved && !hint.text && this.rx(2)) hint = { text: this.rx(2), tone: 'ok' };
      if (this.renderNote) hint = this.renderNote;
      if (this.rf.needsSession()) hint = { text: this.rt(5), tone: '' };
      else if (left === 0) { off = true; hint = { text: this.quotaMessage || fill(this.rt(4), { max: this.rf.quota }), tone: 'error' }; }
      else if (this.lookError || n === 0) { off = true; hint = { text: this.rt(2), tone: '' }; }
      else if (max !== null && n > max) { off = true; hint = { text: fill(this.rt(13), { max: max }), tone: 'error' }; }
      var hadFocus = document.activeElement === btn;
      btn.disabled = off;
      this.setHint(hint.text, hint.tone);
      if (off && hadFocus && this.hintEl) { this.hintEl.setAttribute('tabindex', '-1'); this.hintEl.focus({ preventScroll: true }); }
      // AI Act art. 50 (D-IA, Javi 2026-10-23): the label is fixed over the generated image and the note goes under it; neither can be closed
      // ... and only claims "Imagen generada con IA" when there actually is a generated image (not in demo mode)
      var aiReady = !!(this.lastRender && this.lastRender.status === 'ready' && !this.isDemo(this.lastRender));
      // the helper line under the button says the same thing, so in demo mode it stays out too
      if (this.badgeEl) this.badgeEl.hidden = this.isDemo(this.lastRender);
      if (this.aiNoteEl) this.aiNoteEl.hidden = !aiReady;
      if (this.aiCanvasEl) this.aiCanvasEl.hidden = !aiReady;
    }

    onGenerate(event) {
      if (this.rendering) return;
      // a click that comes from Enter / Space has no pointer detail: the look was asked from the keyboard (N7, focus once it is ready)
      this.keyCreate = !!(event && event.detail === 0);
      // looks mode: the garments on screen were already created -> the button opens that look instead of spending a try
      var done = this.looksOn ? this.readyLookFor(this.look.map(function (p) { return p.id; })) : null;
      if (done && this.looksEl && this.looksEl.open) { this.looksEl.open(done, this.renderBtn); return; }
      this.generate();
    }

    setCanvasError(message) {
      var p = this.q('.nc-tryon__message--error p');
      if (p) {
        if (p.getAttribute('data-default') === null) p.setAttribute('data-default', p.textContent);
        p.textContent = message || p.getAttribute('data-default');
      }
      this.tryon.setStatus('error', message);
    }

    // back from the login (?nc-resume=crear) with a session: the look is intact, so carry on to "Tu foto" as if CREAR had just been pressed
    consumeResume() {
      if (!this.rf || !this.rf.enabled || !this.look.length) return;
      var self = this;
      var run = function () { if (window.NcAuth && window.NcAuth.takeResume('crear')) self.generate(); };
      // nc-auth.js is the last deferred script: the look can load before it runs, so wait for the page to finish loading
      if (window.NcAuth || document.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true });
    }

    // a visitor cannot create: the access sheet opens (nc-auth.js listens to the cancelable `nc-fitting:signin`); nothing is requested
    // expired = the page still says "signed in" but the server answered 401: the access sheet opens anyway (F5). The sheet gives the focus back
    // to GENERAR when it closes (F6): the event's own target is the whole component, which cannot take focus
    askSignIn(reason, expired) {
      this.renderNote = { text: this.rt(5), tone: '' };
      var go = this.emit('signin', { reason: reason || 'render', resume: 'crear', expired: !!expired, trigger: this.renderBtn || null }, true);
      if (go && !expired && !(window.NcAuth && window.NcAuth.isIn && !window.NcAuth.isIn())) this.toast(this.rt(5));
      this.emit('render', { status: 'refused', renderId: null, assetId: null, reason: 'unauthenticated' });
      this.syncRender();
    }

    generate(job) {
      var self = this;
      if (!this.rf.enabled || this.rendering) return Promise.resolve();
      // the page says nobody is signed in (data-nc-auth="out"): the gate does not wait for the backend config to say so
      if (this.rf.needsSession() || (window.NcAuth && !window.NcAuth.isIn())) { this.askSignIn('render'); return Promise.resolve(); }
      // looks mode: a retry of a gallery look carries its own garments ({ ids, replaceId }); it waits here while the photo step runs
      var task = job && Array.isArray(job.ids) ? job : this.pendingJob || null;
      this.pendingJob = null;
      if (!task && !this.look.length) { this.syncRender(); return Promise.resolve(); }
      // no photo yet: the upload state asks for it, and "Usar esta foto" carries on to the render (and the consent)
      if (!this.avatarUploadId) { this.pendingJob = task; this.openUpload('generate', this.renderBtn); return Promise.resolve(); }
      if (this.looksOn) return this.enqueue(task ? task.ids : this.look.map(function (p) { return p.id; }), task && task.replaceId);
      if (this.lastRender && this.lastRender.status === 'ready') this.prevRender = this.lastRender;
      this.rendering = true;
      this.renderFailed = false;
      this.renderNote = null;
      this.lastRender = null;
      this.setProgress(3);
      this.render(false);
      this.syncRender();
      // with an image already on the canvas (model photo, previous result) it stays under the dimmed overlay; without one the neutral slot does
      if (this.modelPhoto || this.prevRender) this.tryon.setStatus('rendered', this.rt(1)); else this.tryon.setStatus('loading', this.rt(1));
      this.setHint(this.rt(1), '');
      this.announce(this.rt(1));
      var ids = this.look.map(function (p) { return p.id; });
      return this.rf.generate({ avatarUploadId: this.avatarUploadId, productIds: ids, onProgress: function (pct) { self.setProgress(pct); } }).then(function (r) {
        if (r.status !== 'ready') { self.rendering = false; self.onRenderResult(r); return; }
        // a ready render may come WITHOUT an image (the simulated gateway of A1): look it up first so the end of the flow says which it is
        return self.renderAsset(r.assetId).then(function (asset) {
          self.rendering = false;
          r.hasImage = !!(asset && asset.src);
          self.onRenderResult(r);
        });
      });
    }

    // the asset record of a generated image, or null (no id, no gateway method, a failed lookup)
    renderAsset(assetId) {
      if (!assetId || !this.gw.getAsset) return Promise.resolve(null);
      return this.gw.getAsset(assetId).then(function (a) { return a || null; }, function () { return null; });
    }

    // demo mode: the render is ready but there is no generated image (simulated gateway). `hasImage` undefined = unknown = real
    isDemo(render) { return !!(render && render.status === 'ready' && render.demo); }

    onRenderResult(r) {
      var st = r.status;
      if (r.left !== undefined && r.left !== null) this.emit('quota', { left: r.left, max: this.rf.max });
      if (st === 'ready') {
        this.setProgress(100);
        var demo = r.hasImage === false;
        var done = demo ? this.demoCopy(1) : this.rt(9);
        this.lastRender = { status: 'ready', renderId: r.renderId, assetId: r.assetId, demo: demo };
        this.prevRender = null;
        this.tryon.setStatus('rendered', done);
        if (!demo) this.showRenderImage(r.assetId);
        this.emit('render', { status: 'ready', renderId: r.renderId, assetId: r.assetId, demo: demo });
        this.announce(demo ? done + '. ' + this.demoCopy(0) : done);
        this.render(false);
        this.syncRender();
        this.revealStage();
        return;
      }
      var isFail = st === 'failed' || st === 'blocked' || st === 'error';
      this.renderFailed = isFail;
      if (!isFail) this.tryon.setStatus('idle', '');
      this.render(false);
      if (isFail) {
        // 7b: the card says it in the spec's words; a blocked look or a connection failure says its own reason
        var text = st === 'blocked' ? this.rt(7) : st === 'error' ? this.errText(r) : (this.failText(r.errorCode) || this.stx(5));
        this.renderNote = { text: text, tone: 'error' };
        this.errorBody = text;
        this.tryon.setStatus(this.modelPhoto || this.prevRender ? 'rendered' : 'error', this.stx(4) + '. ' + text);
        this.emit('render', { status: st === 'error' ? 'failed' : st, renderId: r.renderId || null, assetId: null });
        this.announce(this.stx(4) + '. ' + text);
        this.syncRender();
        var retry = this.q('[data-fr-retry]');
        if (retry) retry.focus({ preventScroll: true });
        return;
      }
      if (st === 'quota') {
        this.quotaMessage = r.message;
        this.rf.left = 0;
        this.emit('quota', { left: 0, max: this.rf.max });
        this.emit('render', { status: 'refused', renderId: null, assetId: null, reason: 'quota' });
      } else if (st === 'unauthenticated') {
        this.askSignIn('render', true);
        return;
      } else if (st === 'consent-required') {
        this.emit('render', { status: 'refused', renderId: null, assetId: null, reason: 'consent' });
        this.syncRender();
        this.openConsent();
        return;
      } else if (st === 'invalid') {
        var msg = r.message || this.rt(12);
        this.renderNote = { text: msg, tone: 'error' };
        this.emit('render', { status: 'refused', renderId: null, assetId: null, reason: 'invalid' });
        if (r.fields && r.fields.avatarUploadId) this.emit('model', { action: 'upload' }, true);
        this.announce(msg);
      }
      this.syncRender();
    }

    // generated image: an asset of the gateway (no src in A1 = the neutral canvas stays)
    showRenderImage(assetId) {
      var self = this;
      if (!assetId || !this.gw.getAsset || !this.tryon) return;
      this.gw.getAsset(assetId).then(function (a) {
        if (!a || !a.src || !self.lastRender || self.lastRender.assetId !== assetId) return;
        var fig = self.tryon.querySelector('.nc-tryon__figure');
        var img = self.tryon.querySelector('.nc-tryon__image');
        if (!img) { img = document.createElement('img'); img.className = 'nc-tryon__image'; img.width = 220; img.height = 330; fig.insertBefore(img, fig.firstChild); }
        img.src = a.src;
        img.alt = a.alt || '';
        img.setAttribute('data-ai-generated', 'true'); // machine-readable mark of the page (the file metadata is the renderer's, Phase B / legal)
        img.hidden = false;
      }, noop);
    }

    // provisional consent (text and design UNKNOWN, not in Figma): a bottom sheet with accept / decline; focus returns to "Generar"
    // consent (state 1): modal on desktop, bottom sheet on mobile; "Continuar" stays disabled until the box is ticked; focus goes to the
    // box, Escape / Cancelar / X close it and the focus returns to GENERAR (nc-sheet)
    openConsent() {
      var sheet = this.consentSheet;
      if (!sheet || !sheet.open) return;
      if (this.consentCheck) this.consentCheck.checked = false;
      if (this.consentAccept) this.consentAccept.disabled = true;
      sheet.open(this.renderBtn);
      if (this.consentCheck) this.consentCheck.focus();
    }

    onConsentAction(event) {
      var self = this;
      if (!this.consentWrap || !event.target.closest('[data-fr-consent]')) return;
      event.stopPropagation();
      var id = event.detail && event.detail.id;
      if (id === 'decline') {
        this.rendering = false;
        this.renderNote = { text: this.rt(11), tone: '' };
        this.syncRender();
        this.announce(this.rt(11));
        return;
      }
      if (id !== 'accept') return;
      this.rendering = true;
      this.syncRender();
      this.rf.acceptConsent().then(function (r) {
        self.rendering = false;
        if (r.status === 'ok') { self.generate(); return; }
        self.renderNote = { text: self.errText(r), tone: 'error' };
        self.syncRender();
      });
    }

    // ---- canvas states (Bloque 3): one overlay at a time on the stage; priority upload > generating > error > quota ----
    syncStage() {
      var stage = this.stageEl;
      if (!stage || !this.rf || !this.rf.enabled) return;
      if (this.looksOn) {
        // looks mode: the stage is the viewer or the example; generating / error / demo live in the look's card and the quota in the hint
        stage.removeAttribute('data-fr-state');
        Array.prototype.forEach.call(stage.querySelectorAll('[data-fr-overlay]'), function (el) { el.hidden = true; });
        return;
      }
      var ready = !!(this.lastRender && this.lastRender.status === 'ready');
      var state = '';
      if (this.rendering) state = 'generating';
      else if (this.renderFailed) state = 'error';
      else if (this.isDemo(this.lastRender)) state = 'demo';
      // the exhausted-quota card never covers a result the user has just got: GENERAR is disabled and the hint says why
      else if (this.rf.left === 0 && !ready && !this.lookError) state = 'quota';
      if (state) stage.setAttribute('data-fr-state', state); else stage.removeAttribute('data-fr-state');
      Array.prototype.forEach.call(stage.querySelectorAll('[data-fr-overlay]'), function (el) { el.hidden = el.getAttribute('data-fr-overlay') !== state; });
      if (state === 'quota') this.paintQuota();
      if (state === 'demo') this.paintDemo();
      var body = this.q('[data-fr-error-body]');
      if (body && this.errorBody && state === 'error') body.textContent = this.errorBody;
      var last = this.q('[data-fr-use-last]');
      if (last) last.hidden = !this.prevRender;
      var wasQuota = this.quotaShown;
      this.quotaShown = state === 'quota';
      if (this.quotaShown && !wasQuota) this.announce(this.paintedQuotaTitle || '');
    }

    // copy of the demo end state: data-msg-demo = note|status line|photo alt|garments caption|upload prompt
    demoCopy(i) {
      return this.m.demo[i] || ['Modo demostración: la imagen generada llegará cuando el probador esté conectado.', 'Simulación completada', 'Tu foto', 'Prendas de tu look', 'Sube una foto para generar tu look'][i];
    }

    // demo card: ONLY the person's chosen photo as the result (the local object URL, never uploaded or stored) and the "Modo demostración" label;
    // no garment is identified inside the stage
    paintDemo() {
      var photo = this.q('[data-fr-demo-photo]');
      if (!this.avatarUrl && photo) { photo.remove(); photo = null; }
      if (this.avatarUrl && !photo) {
        photo = document.createElement('img');
        photo.className = 'nc-fr__demo-photo';
        photo.setAttribute('data-fr-demo-photo', '');
        photo.width = 96;
        photo.height = 128;
        photo.alt = this.demoCopy(2);
        var card = this.q('.nc-fr__card--demo');
        if (card) card.insertBefore(photo, card.firstChild);
      }
      if (photo && photo.getAttribute('src') !== this.avatarUrl) photo.src = this.avatarUrl;
    }

    // the number of tries comes from the configuration (getTryOnConfig().dailyLookQuota, assets/nc-tryon-config.js), never from here
    paintQuota() {
      var max = this.rf.quota || 0; // copied by the flow from the configuration
      var title = this.q('[data-fr-quota-title]');
      if (title) {
        title.textContent = fill(this.stx(0), { max: max });
        this.paintedQuotaTitle = title.textContent;
      }
      var dots = this.q('[data-fr-quota-dots]');
      if (dots && dots.children.length !== max) {
        dots.textContent = '';
        for (var k = 0; k < max; k++) {
          var li = document.createElement('li');
          li.className = 'nc-fr__dot-used';
          dots.appendChild(li);
        }
      }
    }

    setProgress(pct) {
      var n = Math.max(0, Math.min(100, Math.round(pct)));
      var fillEl = this.q('[data-fr-bar]');
      if (fillEl) fillEl.style.width = n + '%';
      var bar = this.q('.nc-fr__bar');
      if (bar) bar.setAttribute('aria-valuenow', String(n));
      var label = this.q('[data-fr-pct]');
      if (label) label.textContent = n + '%';
      if (this.renderBtn) this.renderBtn.style.setProperty('--nc-fr-progress', n + '%');
    }

    useLastResult() {
      var prev = this.prevRender;
      if (!prev) return;
      this.lastRender = prev;
      this.prevRender = null;
      this.renderFailed = false;
      this.renderNote = null;
      var demo = this.isDemo(prev);
      this.tryon.setStatus('rendered', demo ? this.demoCopy(1) : this.rt(9));
      if (!demo) this.showRenderImage(prev.assetId);
      this.render(false);
      this.syncRender();
      this.announce(demo ? this.demoCopy(1) : this.rt(9));
      if (this.renderBtn && !this.renderBtn.disabled) this.renderBtn.focus({ preventScroll: true });
    }

    // ---- change-model / photo upload: a modal (>= 700 px) or bottom sheet (nc-sheet) with the current photo, the drop area and the file input ----
    openUpload(reason, trigger) {
      if (!this.uploadEl || !this.uploadSheet || !this.uploadSheet.open || this.uploadOpen) return;
      var active = document.activeElement;
      this.uploadTrigger = trigger || (active && !this.uploadEl.contains(active) && active !== document.body ? active : null);
      var icon = this.q('[data-fr-model-sheet]');
      if (!this.uploadTrigger && icon && icon.getClientRects().length) this.uploadTrigger = icon;
      if (!this.uploadTrigger) this.uploadTrigger = this.renderBtn || null;
      this.uploadReason = reason || 'model';
      // ONE photo dialog on every width: "Tu foto" from GENERAR; from the model button its own label ("Tu foto" / "Cambiar foto")
      var title = this.uploadSheet.querySelector('.nc-sheet__title');
      if (title) title.textContent = this.photoCopy(this.uploadReason === 'generate' || !this.hasPhoto() ? 0 : 1);
      // touch: "Hacer una foto" (camera input) next to "Elegir archivo", and no drag-and-drop copy
      this.touch = this.isTouch();
      var camera = this.q('[data-fr-act="camera"]');
      if (camera) camera.hidden = !(this.touch && this.cameraInput);
      var actions = this.q('.nc-fr__upload-actions');
      if (actions) actions.setAttribute('data-camera', camera && !camera.hidden ? 'true' : 'false');
      this.resetUpload();
      this.paintCurrentPhoto();
      var picked = this.preparePresetModels();
      this.preparePhotoConsent();
      this.syncUseButton();
      this.uploadOpen = true;
      this.uploadSheet.open(this.uploadTrigger);
      // a preselected model (remembered, or the first one from CREAR) takes the focus; else the camera / file button as before
      var first = picked || this.uploadEl.querySelector((camera && !camera.hidden ? '[data-fr-act="camera"]' : '[data-fr-act="choose"]') + ' .nc-button');
      if (first) first.focus({ preventScroll: true });
      // GENERAR without a photo says why the modal opened
      if (this.uploadReason === 'generate') this.announce(this.demoCopy(4) + '. ' + this.dropPrompt());
      else this.announce(this.dropPrompt());
    }

    // the prompt of the drop area: desktop "Arrastra una foto o elige un archivo", touch "Elige una foto o hazla ahora"
    dropPrompt() { return this.touch ? this.photoCopy(3) : this.stx(8) + ' ' + this.stx(9); }

    // the modal is closed by "Cancelar", the X, the scrim or Escape (nc-sheet traps focus and restores it to the trigger): the state is released here
    onUploadSheetClose(event) {
      if (!this.uploadWrap || !event.target.closest('[data-fr-upload-sheet]')) return;
      event.stopPropagation();
      this.closeUpload(true);
      var next = this.afterUploadClose;
      this.afterUploadClose = null;
      if (next) next();
    }

    closeUpload(fromSheet) {
      if (!this.uploadOpen) return;
      this.uploadOpen = false;
      window.clearTimeout(this.pmTimer);
      this.pmAutoUse = null;
      this.pmArmed = null;
      this.pmBusy = null;
      this.resetUpload();
      this.uploadTrigger = null;
      if (!fromSheet && this.uploadSheet && this.uploadSheet.close) this.uploadSheet.close();
    }

    // the photo already in use (object URL of this session, or only its upload id) with "Quitar foto"; nothing is listed: the backend keeps one photo per user
    paintCurrentPhoto() {
      var box = this.q('[data-fr-current]');
      if (!box) return;
      var has = !!(this.avatarUploadId || this.avatarUrl);
      box.hidden = !has;
      var holder = this.q('[data-fr-current-photo]');
      if (!holder) return;
      var img = holder.querySelector('img');
      var src = this.avatarUrl || (this.avatarSaved ? this.savedAvatarSrc : '');
      if (has && src) {
        if (!img) { img = document.createElement('img'); img.width = 48; img.height = 64; img.alt = ''; holder.appendChild(img); }
        img.src = src;
      } else if (img) img.remove();
    }

    removeCurrentPhoto() {
      var self = this;
      var id = this.avatarUploadId;
      if (!id && !this.avatarUrl) return;
      var btn = this.q('[data-fr-act="remove"]');
      var done = function () {
        if (self.avatarUrl) URL.revokeObjectURL(self.avatarUrl);
        self.avatarUploadId = null;
        self.avatarUrl = null;
        self.avatarSaved = false;
        self.avatarModelId = null;
        self.markPresetModel(null);
        var thumb = self.q('[data-fr-model-thumb]');
        var timg = thumb && thumb.querySelector('img');
        if (timg) timg.remove();
        self.paintCurrentPhoto();
        self.syncPhotoLabels();
        self.syncRender();
        self.announce(self.stx(29));
        var choose = self.uploadEl && self.uploadEl.querySelector('[data-fr-act="choose"] .nc-button');
        if (choose) choose.focus({ preventScroll: true });
      };
      if (btn) btn.disabled = true;
      var call = id && this.gw.deleteAvatar ? this.gw.deleteAvatar(id) : Promise.resolve();
      call.then(done, function () {
        if (btn) btn.disabled = false;
        self.announce(self.stx(30));
      });
    }

    // drop area states: '' (prompt with the "elige un archivo" link), busy (checking), ready (photo chosen), error (the photo does not serve)
    setDrop(kind, title, sub) {
      var drop = this.q('[data-fr-drop]');
      if (!drop) return;
      if (kind) drop.setAttribute('data-kind', kind); else drop.removeAttribute('data-kind');
      var text = this.q('[data-fr-drop-text]');
      var link = this.q('[data-fr-act="choose-link"]');
      var heading = this.q('[data-fr-drop-title]');
      var subEl = this.q('[data-fr-drop-sub]');
      if (text) text.textContent = kind ? title : this.touch ? this.photoCopy(3) : this.stx(8);
      if (link) { link.hidden = !!kind || !!this.touch; link.textContent = this.stx(9); }
      if (heading) { if (kind === 'busy') heading.setAttribute('aria-busy', 'true'); else heading.removeAttribute('aria-busy'); }
      if (subEl) {
        subEl.hidden = !sub;
        subEl.textContent = sub || '';
        // a long file name is cut with an ellipsis by the CSS: the full name stays available as the title
        if (sub) subEl.title = sub; else subEl.removeAttribute('title');
      }
    }

    resetUpload() {
      this.uploadSeq = (this.uploadSeq || 0) + 1;
      if (this.photoWait) this.photoWait.stop();
      this.waitUp = null;
      this.waitEscaped = false;
      if (this.pendingUpload && this.pendingUpload.id && this.gw.deleteAvatar) this.gw.deleteAvatar(this.pendingUpload.id).then(noop, noop);
      if (this.pendingUpload && this.pendingUpload.url) URL.revokeObjectURL(this.pendingUpload.url);
      this.pendingUpload = null;
      var preview = this.q('[data-fr-preview]');
      if (preview) preview.remove();
      if (this.fileInput) this.fileInput.value = '';
      if (this.cameraInput) this.cameraInput.value = '';
      if (this.useBtn) this.useBtn.disabled = true;
      this.setDrop('', '', '');
    }

    showBadPhoto(title, sub) {
      this.pendingUpload = null;
      this.pmAutoUse = null;
      if (this.useBtn) this.useBtn.disabled = true;
      var preview = this.q('[data-fr-preview]');
      if (preview) preview.remove();
      this.setDrop('error', title, sub);
      this.announce(title + ' ' + sub);
    }

    onFile(file, meta) {
      var self = this;
      if (!file || !this.uploadOpen) return;
      this.holdFile = null;
      // meta (B3): { modelId, label } when the file is one of the preset models; an own file (input, camera, drop) unchecks the tiles
      var model = meta && meta.modelId ? meta.modelId : null;
      var shown = (meta && meta.label) || file.name;
      if (!model) { window.clearTimeout(this.pmTimer); this.pmArmed = null; this.pmBusy = null; this.markPresetModel(null); }
      this.resetUpload();
      var seq = this.uploadSeq;
      if (['image/jpeg', 'image/png'].indexOf(file.type) < 0) { this.showBadPhoto(this.stx(19), this.stx(25)); return; }
      if (file.size > 10 * 1024 * 1024) { this.showBadPhoto(this.stx(19), this.stx(26)); return; }
      // F7: an empty file never leaves the browser
      if (!file.size) { this.showBadPhoto(this.stx(19), this.stx(40)); return; }
      // B-fix (28-10): the photo leaves the browser only after the person ticked the consent of this dialog (looks mode). Until then the file
      // is held here, untouched: nothing is uploaded, checked or stored. onChange() continues it once the consent is recorded.
      if (this.photoNeedsConsent()) {
        var held = { file: file, meta: meta || null };
        this.holdFile = held;
        this.setDrop('idle', this.m.consent[3] || '', shown);
        this.syncUseButton();
        // F12: the box was already ticked when the photo arrived (ticked first, or a preselected model pressed on "Usar"): record it now
        if (this.photoConsentPending()) { this.recordConsentFor(); return; }
        this.announce(this.m.consent[3] || '');
        return;
      }
      this.setDrop('busy', this.stx(23), '');
      this.announce(this.stx(23));
      // honest wait: 10 s "tarda más de lo normal" (aria-live), 20 s the user may continue; resetUpload (new file, Cancelar, close) stops both timers
      this.photoWait = makePhotoWait(function (f, ms) { return window.setTimeout(f, ms); }, function (id) { window.clearTimeout(id); }, function () {
        if (seq !== self.uploadSeq || !self.uploadOpen || !self.stx(32)) return;
        var drop = self.q('[data-fr-drop]');
        if (!drop || drop.getAttribute('data-kind') !== 'busy') return;
        var text = self.q('[data-fr-drop-text]');
        if (text) text.textContent = self.stx(32);
        self.announce(self.stx(32));
      }, function () {
        if (seq !== self.uploadSeq || !self.uploadOpen) return;
        self.waitEscaped = true;
        self.offerContinue(seq);
      });
      this.photoWait.start();
      // F2: "Esa imagen no sirve" only when the photo itself was refused; a session or server failure says it is not the photo's fault
      var bad = function (sub, title) { if (seq === self.uploadSeq) self.showBadPhoto(title || self.stx(19), sub || self.stx(20)); };
      this.gw.createUpload({ purpose: 'avatar', file: file }).then(function (up) {
        if (seq === self.uploadSeq) { self.waitUp = { id: up.id, file: file, modelId: model, label: shown }; if (self.waitEscaped) self.offerContinue(seq); }
        return self.gw.validateAvatar(up.id).then(function (v) {
          var used = self.avatarUploadId === up.id;
          if (seq !== self.uploadSeq) { if (!used && self.gw.deleteAvatar) self.gw.deleteAvatar(up.id).then(noop, noop); return; }
          self.photoWait.stop();
          if (!v || !v.ok) { if (self.gw.deleteAvatar) self.gw.deleteAvatar(up.id).then(noop, noop); bad(self.photoReasonText(v)); return; }
          var early = self.pendingUpload && self.pendingUpload.id === up.id ? self.pendingUpload : null;
          var url = early ? early.url : URL.createObjectURL(file);
          self.pendingUpload = { id: up.id, url: url, name: file.name, modelId: model };
          var drop = self.q('[data-fr-drop]');
          var oldPreview = self.q('[data-fr-preview]');
          if (oldPreview) oldPreview.remove();
          var preview = document.createElement('img');
          preview.className = 'nc-fr__drop-preview';
          preview.setAttribute('data-fr-preview', '');
          preview.width = 96;
          preview.height = 128;
          preview.alt = '';
          preview.src = url;
          if (drop) drop.insertBefore(preview, drop.querySelector('[data-fr-drop-title]'));
          self.setDrop('ready', self.stx(24), shown);
          self.syncUseButton();
          self.announce(self.stx(24));
          self.maybeAutoUse(seq);
        });
      }).then(null, function (err) {
        // the mock answers with fieldErrors, the real gateway with fields + code + the server's message; say what the server said
        var fe = err && ((err.fieldErrors && err.fieldErrors.file) || (err.fields && (err.fields.file || err.fields.uploadId)));
        if (err && err.code === 'unreadable') { bad(self.stx(40)); return; }
        if (fe) { bad(fe); return; }
        if (err && (err.code === 'unauthenticated' || err.status === 401 || err.kind === 'unauthenticated')) { if (seq === self.uploadSeq) self.sessionExpired(); return; }
        bad(err && err.kind === 'network' ? self.rt(12) : self.stx(35), self.stx(34));
      });
    }

    // 20 s without an answer: "Usar esta foto" is enabled anyway; the pending validation keeps running and an unfit photo still lands in the error state
    offerContinue(seq) {
      if (seq !== this.uploadSeq || !this.uploadOpen || !this.waitUp || this.pendingUpload || !this.stx(33)) return;
      var up = this.waitUp;
      var url = URL.createObjectURL(up.file);
      this.pendingUpload = { id: up.id, url: url, name: up.file.name, modelId: up.modelId || null, unchecked: true };
      var drop = this.q('[data-fr-drop]');
      var preview = document.createElement('img');
      preview.className = 'nc-fr__drop-preview';
      preview.setAttribute('data-fr-preview', '');
      preview.width = 96;
      preview.height = 128;
      preview.alt = '';
      preview.src = url;
      if (drop) drop.insertBefore(preview, drop.querySelector('[data-fr-drop-title]'));
      this.setDrop('busy', this.stx(32), this.stx(33));
      this.syncUseButton();
      this.announce(this.stx(33));
      this.maybeAutoUse(seq);
    }

    useUploadedPhoto() {
      var up = this.pendingUpload;
      // B3: a preselected model is uploaded only now, by the same path as an own photo, and used as soon as it is valid
      if (!up && this.pmArmed && this.uploadOpen) { var armed = this.pmArmed; this.pmArmed = null; this.startPresetUpload(armed, true); return; }
      if (!up || !this.uploadOpen) return;
      this.avatarModelId = up.modelId || null;
      if (up.modelId && presetModels()) presetModels().remember(up.modelId);
      var old = this.avatarUploadId;
      var oldUrl = this.avatarUrl;
      this.avatarUploadId = up.id;
      this.avatarUrl = up.url;
      this.avatarSaved = false;
      this.pendingUpload = null; // the file now belongs to the component: resetUpload must not delete it
      if (old && old !== up.id && this.gw.deleteAvatar) this.gw.deleteAvatar(old).then(noop, noop);
      if (oldUrl) URL.revokeObjectURL(oldUrl);
      var thumb = this.q('[data-fr-model-thumb]');
      if (thumb) {
        var img = thumb.querySelector('img');
        if (!img) { img = document.createElement('img'); img.width = 62; img.height = 64; img.alt = ''; thumb.appendChild(img); }
        img.src = up.url;
      }
      var reason = this.uploadReason;
      // A6: the consent ticked in this same dialog is recorded first (the separate consent sheet is then never needed)
      var consent = this.photoConsentPending();
      var self = this;
      // B1: "Guardar mi foto" is opt-in (unchecked); it is sent only once the consent is recorded, and a failure leaves the 24 h rule with a notice
      var keep = this.retainAsked() && !up.modelId;
      var retain = function () {
        if (!keep || typeof self.gw.retainAvatar !== 'function') return;
        self.gw.retainAvatar(up.id, true).then(function () { self.avatarSaved = true; self.savedAvatarSrc = ''; self.announce(self.rx(3)); }, function () { self.toast(self.rx(4)); });
      };
      var carryOn = function () {
        if (!consent) { retain(); self.generate(); return; }
        self.rf.acceptConsent().then(function (r) {
          if (r.status === 'ok') { self.photoConsentOk = true; retain(); self.generate(); return; }
          self.renderNote = { text: self.errText(r), tone: 'error' };
          self.syncRender();
        });
      };
      // GENERAR carries on once the modal has finished closing, so the consent sheet never fights its scroll lock and focus restore
      if (reason === 'generate') this.afterUploadClose = carryOn;
      else if (consent) this.rf.acceptConsent().then(function (r) { if (r.status === 'ok') { self.photoConsentOk = true; retain(); } });
      else retain();
      this.syncPhotoLabels();
      this.closeUpload();
      this.emit('model', { action: 'photo', uploadId: up.id, modelId: up.modelId || null });
    }

    onChange(event) {
      var t = event.target;
      if (t && t.hasAttribute && t.hasAttribute('data-fr-file')) { this.onFile(t.files && t.files[0]); return; }
      if (t && t.hasAttribute && t.hasAttribute('data-fr-consent-check') && this.consentAccept) this.consentAccept.disabled = !t.checked;
      if (t && t.hasAttribute && t.hasAttribute('data-fr-photo-consent-check')) {
        var held = this.holdFile;
        if (!t.checked || !held) { this.syncUseButton(); return; }
        this.recordConsentFor();
        return;
      }
    }

    // the consent is recorded first, so the server never receives a photo without it; a failed record keeps the photo held
    recordConsentFor() {
      var self = this;
      if (this.consentSaving) return; // the answer picks up whatever photo is held by then (a model tapped, or another one, while it saves)
      this.consentSaving = true;
      this.syncUseButton();
      this.rf.acceptConsent().then(function (r) {
        self.consentSaving = false;
        if (r.status !== 'ok') {
          // the box goes back to unticked: ticking it again retries, and the dialog says why nothing happened
          var check = self.q('[data-fr-photo-consent-check]');
          if (check) check.checked = false;
          // 401: the page says "signed in" but the server no longer knows the session (it needs one for every personal route)
          if (r.status === 'unauthenticated') { self.holdFile = null; self.sessionExpired(); self.syncUseButton(); return; }
          var text = self.errText(r);
          self.renderNote = { text: text, tone: 'error' };
          self.syncRender();
          self.toast(text);
          if (self.holdFile) self.setDrop('idle', text, '');
          self.syncUseButton();
          return;
        }
        self.photoConsentOk = true;
        self.releaseHeldFile();
        self.syncUseButton();
      });
    }

    // the server answered 401 inside the photo dialog: the dialog closes and the access sheet opens (same as a 401 on CREAR), instead of a
    // "something failed" the person cannot act on
    sessionExpired() {
      var self = this;
      this.afterUploadClose = function () { self.askSignIn('render', true); };
      if (this.uploadSheet && typeof this.uploadSheet.close === 'function') this.uploadSheet.close(); else { this.closeUpload(); this.afterUploadClose(); this.afterUploadClose = null; }
    }

    // the consent is recorded: the photo that waited for it (the latest one) goes on its way. A preselected model pressed on "Usar" carries on by
    // itself once valid: onFile restarts the upload, so it is re-armed.
    releaseHeldFile() {
      var held = this.holdFile;
      if (!held) return;
      var auto = this.pmAutoUse != null;
      this.holdFile = null;
      this.onFile(held.file, held.meta);
      if (auto) this.pmAutoUse = this.uploadSeq;
    }

    onDrag(event) {
      var drop = event.target.closest && event.target.closest('[data-fr-drop]');
      if (!drop || !this.uploadOpen) return;
      event.preventDefault();
      if (event.type === 'dragenter' || event.type === 'dragover') { drop.setAttribute('data-dragging', ''); return; }
      drop.removeAttribute('data-dragging');
      if (event.type === 'drop') {
        var files = event.dataTransfer && event.dataTransfer.files;
        if (files && files[0]) this.onFile(files[0]);
      }
    }

    // ---- A6: the consent is a checkbox of the photo dialog (looks mode); "Usar y crear" when the dialog was opened by CREAR ----
    preparePhotoConsent() {
      var box = this.q('[data-fr-photo-consent]');
      var label = this.useBtn && this.useBtn.querySelector('.nc-button__label');
      if (label) label.textContent = this.looksOn && this.uploadReason === 'generate' ? this.photoCopy(4) : this.stx(18);
      if (!box) return;
      var check = box.querySelector('[data-fr-photo-consent-check]');
      if (check) check.checked = false;
      box.hidden = true;
      if (!this.looksOn || !this.rf.enabled) return;
      var self = this;
      if (this.photoConsentOk) return;
      this.consentChecking = true;
      this.rf.consentGiven().then(function (ok) {
        self.consentChecking = false;
        self.photoConsentOk = ok;
        box.hidden = self.photoConsentOk;
        // a photo picked while the answer was on its way waited for a consent that turns out to be recorded already
        if (ok) self.releaseHeldFile();
        self.syncUseButton();
      }, function () { self.consentChecking = false; box.hidden = false; self.syncUseButton(); });
    }

    // the box is ticked and the consent is not recorded yet
    retainAsked() {
      var box = this.q('[data-fr-retain-check]');
      return !!(box && box.checked && this.getAttribute('avatar-retention') === 'on');
    }

    // looks mode with a session-capable backend: the photo may not be sent before the consent is recorded
    photoNeedsConsent() {
      return !!(this.looksOn && this.rf && this.rf.enabled && !this.photoConsentOk);
    }

    photoConsentPending() {
      var check = this.q('[data-fr-photo-consent-check]');
      var box = this.q('[data-fr-photo-consent]');
      return !!(this.looksOn && check && box && !box.hidden && check.checked && !this.photoConsentOk);
    }

    // "Usar esta foto" / "Usar y crear": a valid photo and, in looks mode, the consent (already given or ticked here)
    syncUseButton() {
      if (!this.useBtn) return;
      var box = this.q('[data-fr-photo-consent]');
      var check = this.q('[data-fr-photo-consent-check]');
      var consent = !this.looksOn || this.photoConsentOk || ((!box || box.hidden) && !this.consentChecking) || (check && check.checked);
      // B3: a preselected model (armed, not uploaded yet) enables "Usar" too: pressing it uploads the model and carries on
      if (this.pmArmed && !this.pendingUpload) { this.useBtn.disabled = !consent; return; }
      this.useBtn.disabled = !(this.pendingUpload && consent);
    }

    // ---- Tanda A (looks="on"): the gallery, the viewer and the non-blocking creation ----
    setupLooks() {
      var self = this;
      this.looksEl = this.q('nc-looks');
      this.exampleEl = this.q('[data-fr-example]');
      this.viewerHost = this.q('[data-fr-viewer-host]');
      this.setupSlots();
      if (this.stageEl) this.stageEl.setAttribute('data-fr-looks', '');
      // the photo controls ride a header row of the stage (they no longer float over a canvas)
      var head = this.q('[data-fr-stage-head]');
      if (head) ['[data-fr-model]', '[data-fr-model-sheet]'].forEach(function (sel) { var el = self.q(sel); if (el) head.appendChild(el); });
      if (this.exampleEl) this.exampleEl.hidden = false;
      if (this.badgeEl) this.badgeEl.hidden = true;
      this.addEventListener('nc-looks:change', function () { self.syncExample(); self.syncRender(); });
      // the copy of the create button follows the wait (20 s, 35 s) like the stage and the chip
      this.addEventListener('nc-looks:wait', function () { self.syncRenderButton(); });
      this.addEventListener('nc-looks:edit', function (e) { self.onLookEdit(e.detail || {}); });
      // N11: «Crea tu primer look» of the empty gallery brings the person to the catalogue (the chooser's focus target)
      this.addEventListener('nc-looks:first', function () { self.onAdd(); });
      this.addEventListener('nc-looks:retry', function (e) { var d = e.detail || {}; if (d.productIds && d.productIds.length) self.generate({ ids: d.productIds, replaceId: d.id }); });
      var link = this.q('[data-fr-example-link]');
      if (link) link.addEventListener('click', function (e) {
        var gallery = self.looksEl && self.looksEl.querySelector('[data-looks-gallery]');
        if (!gallery) return;
        e.preventDefault();
        gallery.scrollIntoView({ block: 'start', behavior: window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        gallery.focus({ preventScroll: true });
      });
      if (window.customElements) customElements.whenDefined('nc-looks').then(function () {
        if (!self.looksEl || !self.looksEl.setInlineHost) return;
        self.looksEl.fallbackTrigger = self.renderBtn;
        self.looksEl.setInlineHost(self.viewerHost);
        // below the two-column desktop the stage shows the newest look (creating figure + time copy, then its picture: N1, N2)
        if (self.looksEl.setPreviewHost) self.looksEl.setPreviewHost(self.exampleEl);
        self.syncExample();
      });
      this.syncExample();
    }

    exampleCopy(i) { return this.m.example[i] || EXAMPLE_DEFAULTS[i]; }

    looksList() { return this.looksEl && Array.isArray(this.looksEl.list) ? this.looksEl.list : []; }

    // A2: "Así se verá tu look" until there is a look; then (narrow) "Mis looks (n)" with the way to the gallery
    syncExample() {
      var ex = this.exampleEl;
      if (!ex) return;
      // F4: a failed attempt keeps its retry card but is not a look: it does not count in "Mis looks (n)"
      var n = this.looksList().filter(function (r) { return r.status !== 'error'; }).length;
      ex.setAttribute('data-has-looks', n ? 'true' : 'false');
      var link = ex.querySelector('[data-fr-example-link]');
      if (link) link.hidden = !n;
      // while the newest look is being created nc-looks writes the wait copy here (title + live time line)
      if (ex.getAttribute('data-looks-preview') === 'generating') return;
      var title = ex.querySelector('[data-fr-example-title]');
      var line = ex.querySelector('[data-fr-example-line]');
      if (title) title.textContent = n ? fill(this.exampleCopy(3), { n: n }) : this.exampleCopy(0);
      if (line) line.textContent = n ? this.exampleCopy(4) : this.exampleCopy(1);
    }

    // the garments already being created (same set, any order): their look id
    findJob(ids) {
      var self = this;
      var core = window.NcLooksCore;
      return Object.keys(this.jobs).filter(function (k) { return core ? core.sameSet(self.jobs[k], ids) : false; })[0] || null;
    }

    // the newest ready look with exactly these garments
    readyLookFor(ids) {
      var core = window.NcLooksCore;
      if (!core || !ids.length) return null;
      var hit = this.looksList().filter(function (r) { return r.status === 'ready' && core.sameSet(r.items.map(function (i) { return i.productId; }), ids); })[0];
      return hit ? hit.id : null;
    }

    // the garments of a new look as the gallery keeps them; the brand store links come from the gateway records (never invented)
    lookItems(ids) {
      var self = this;
      return ids.map(function (id) {
        var p = self.catalog[id] || { id: id, title: '', brand: '', price: '' };
        return { productId: id, title: p.title || '', brand: p.brand || '', brandSlug: '', price: p.price || '', assetId: p.assetId || null, slot: p.slot || '', url: '', officialUrl: null, brandOfficialUrl: null };
      });
    }

    enrichItems(id, ids) {
      var self = this;
      var gw = this.gw;
      Promise.all(ids.map(function (pid) {
        return gw.getProduct(pid).then(function (p) {
          var info = p.brandInfo || null;
          var item = { url: p.url || '', officialUrl: p.officialUrl || null, brandSlug: (info && info.slug) || '', brand: (info && info.name) || p.brand || '' };
          if (!item.officialUrl && item.brandSlug && gw.getBrand) {
            return gw.getBrand(item.brandSlug).then(function (b) { item.brandOfficialUrl = (b && b.officialUrl) || null; return item; }, function () { return item; });
          }
          return item;
        }, function () { return {}; });
      })).then(function (extra) {
        var rec = self.looksEl && self.looksEl.find ? self.looksEl.find(id) : null;
        if (!rec) return;
        var items = rec.items.map(function (it, k) { var x = extra[k] || {}; return Object.assign({}, it, x, { brand: x.brand || it.brand }); });
        self.looksEl.update(id, { items: items });
      });
    }

    // A5: CREAR MI LOOK enqueues: a "Creando tu look" card + the chip, the builder stays free; the result fills the card
    enqueue(ids, replaceId) {
      var self = this;
      var looks = this.looksEl;
      if (!looks || !looks.add || !ids.length) return Promise.resolve();
      var same = this.findJob(ids);
      if (same) { looks.open(same, this.renderBtn); return Promise.resolve(); }
      var core = window.NcLooksCore;
      var byKey = !!this.keyCreate;
      this.keyCreate = false;
      var id = replaceId || (core ? core.newId(Date.now(), ++this.jobSeq) : 'gl-' + Date.now().toString(36) + '-' + (++this.jobSeq));
      var old = replaceId && looks.find ? looks.find(replaceId) : null;
      looks.add({ id: id, status: 'generating', createdAt: old ? old.createdAt : Date.now(), renderAt: null, renderId: null, assetId: null, demo: false, aiGenerated: true, saved: old ? !!old.saved : false, message: '', items: old ? old.items : this.lookItems(ids) });
      this.jobs[id] = ids.slice();
      this.renderNote = null;
      this.syncRender();
      if (!old) this.enrichItems(id, ids);
      // N10: onRequested starts the wait clock of the card, the stage and the chip ("unos 20 s" → 20 s → 35 s) when the render is requested
      var requested = function (res) { if (looks.find && looks.find(id)) looks.update(id, { renderAt: Date.now(), renderId: (res && res.renderId) || undefined }); };
      return this.rf.generate({ avatarUploadId: this.avatarUploadId, productIds: ids, onRequested: requested }).then(function (r) {
        if (r.left !== undefined && r.left !== null) self.emit('quota', { left: r.left, max: self.rf.max });
        if (r.status === 'ready') {
          return self.renderAsset(r.assetId).then(function (asset) {
            delete self.jobs[id];
            var demo = !(asset && asset.src);
            // "Guardar look" of the panel keeps this image when the look on screen is still these garments (PV-4)
            if (core && core.sameSet(ids, self.look.map(function (p) { return p.id; }))) self.lastRender = { status: 'ready', renderId: r.renderId, assetId: r.assetId, demo: demo };
            looks.update(id, { status: 'ready', renderId: r.renderId, assetId: r.assetId || null, demo: demo, aiGenerated: r.aiGenerated !== false, message: '' });
            self.emit('render', { status: 'ready', renderId: r.renderId, assetId: r.assetId, demo: demo });
            self.syncRender();
            if (byKey) self.focusReadyLook(id);
          });
        }
        delete self.jobs[id];
        var st = r.status;
        if (st === 'failed' || st === 'blocked' || st === 'error') {
          var text = st === 'blocked' ? self.rt(7) : st === 'error' ? self.errText(r) : (self.failText(r.errorCode) || self.stx(5));
          looks.update(id, { status: 'error', renderId: r.renderId || null, message: text });
          self.emit('render', { status: st === 'error' ? 'failed' : st, renderId: r.renderId || null, assetId: null });
          self.syncRender();
          return;
        }
        // refused before anything was created (quota, session, consent, photo): no card; the existing notice says why
        if (old) looks.update(id, { status: 'error', message: old.message }); else looks.remove(id);
        self.onRenderResult(r);
      });
    }

    // N7: the focus follows a look created from the keyboard only while it is still where the person left it (CREAR, or nowhere after the
    // photo dialog closed); a pointer create or a focus moved elsewhere in the meantime is never taken
    focusReadyLook(id) {
      var a = document.activeElement;
      var still = !a || a === document.body || a === document.documentElement || (this.renderBtn && (a === this.renderBtn || (this.hintEl && a === this.hintEl)));
      if (still && this.looksEl && this.looksEl.focusResult) this.looksEl.focusResult(id);
    }

    // while the garments on screen are being created the button carries the live time copy with the three dots (the copy of the stage
    // and the chip, N1), instead of the spinner and a second "Creando tu look…"; the box never changes (the overlay stacks on the label)
    paintWaitLabel(btn, text) {
      var overlay = btn && btn.querySelector('.nc-button__overlay--loading');
      if (!overlay) return;
      if (this.loadingLabel === undefined) this.loadingLabel = overlay.textContent;
      if (!text) {
        if (btn.hasAttribute('data-fr-wait')) { btn.removeAttribute('data-fr-wait'); overlay.textContent = this.loadingLabel; }
        return;
      }
      btn.setAttribute('data-fr-wait', '');
      var span = overlay.querySelector('[data-fr-wait-text]');
      if (!span) {
        overlay.textContent = '';
        overlay.insertAdjacentHTML('afterbegin', '<span class="nc-looks-dots" aria-hidden="true"><i></i><i></i><i></i></span>');
        span = document.createElement('span');
        span.setAttribute('data-fr-wait-text', '');
        overlay.appendChild(span);
      }
      if (span.textContent !== text) span.textContent = text;
    }

    // the create button in looks mode: never locked by a running look (A5); loading only for the garments already being created
    syncCreateButton() {
      var btn = this.renderBtn;
      var ids = this.look.map(function (p) { return p.id; });
      var n = ids.length;
      var left = this.rf.left;
      var max = this.rf.max;
      var job = n > 0 ? this.findJob(ids) : null;
      var busy = !!job;
      var done = !busy && n > 0 ? this.readyLookFor(ids) : null;
      var rec = busy && this.looksEl && this.looksEl.find ? this.looksEl.find(job) : null;
      this.paintWaitLabel(btn, rec && this.looksEl.statusText ? this.looksEl.statusText(rec) : '');
      btn.setAttribute('data-state', busy ? 'loading' : 'default');
      var label = btn.querySelector('.nc-button__label');
      if (label) label.textContent = done ? (this.m.create[0] || 'Ver mi look') : this.rt(0);
      var hint = { text: '', tone: '' };
      var off = false;
      if (this.avatarSaved && !hint.text && this.rx(2)) hint = { text: this.rx(2), tone: 'ok' };
      if (this.renderNote) hint = this.renderNote;
      if (this.rf.needsSession()) hint = { text: this.rt(5), tone: '' };
      else if (left === 0 && !done) { off = true; hint = { text: this.quotaMessage || fill(this.rt(4), { max: this.rf.quota }), tone: 'error' }; }
      else if (this.lookError || n === 0) { off = true; hint = { text: this.lookError ? this.rt(2) : '', tone: '' }; } // the example card already says what to do
      else if (max !== null && n > max) { off = true; hint = { text: fill(this.rt(13), { max: max }), tone: 'error' }; }
      else if (!hint.text && showQuota(left) && left > 0) hint = { text: fill(plural(this.rt(3), left), { n: left }), tone: '' };
      var hadFocus = document.activeElement === btn;
      btn.disabled = off;
      this.setHint(hint.text, hint.tone);
      if (off && hadFocus && this.hintEl) { this.hintEl.setAttribute('tabindex', '-1'); this.hintEl.focus({ preventScroll: true }); }
      if (this.badgeEl) this.badgeEl.hidden = true;
      if (this.aiNoteEl) this.aiNoteEl.hidden = true;
      if (this.aiCanvasEl) this.aiCanvasEl.hidden = true;
      if (this.placeDock) this.placeDock();
      this.syncSlotsCount();
    }

    // Editar (viewer): the builder gets that look's garments ("Probar este look" path: one history step, Deshacer in the toast)
    onLookEdit(detail) {
      var self = this;
      var ids = detail.productIds || [];
      if (!ids.length) return;
      this.replaceLook(ids, { title: detail.title || '' }).then(function () {
        var title = self.lookEl && self.lookEl.querySelector('.nc-current-look__title');
        if (title) { title.setAttribute('tabindex', '-1'); title.focus({ preventScroll: false }); }
        self.emit('edit', { lookId: detail.id || null, productIds: ids.slice() });
      });
    }

    // ---- public add of the component (step 1.6): used by window.NcFittingRoom.add when this component is on the page ----
    canAdd() { return !this.viewer && this.looked && !this.lookError; }

    addProduct(productId, options) {
      var self = this;
      var done = function (status, extra) { return Object.assign({ status: status, productId: productId }, extra || {}); };
      if (!this.canAdd()) return Promise.resolve(done('unavailable'));
      if (this.look.some(function (p) { return p.id === productId; })) return Promise.resolve(done('exists'));
      return this.gw.getProduct(productId).then(function (p) {
        var prod = { id: p.id, title: p.title, brand: p.brand, price: p.price, assetId: p.assetId, slot: p.slot, size: options && options.size };
        self.catalog[p.id] = prod;
        return self.toggle(prod, true, true).then(function (r) {
          if (r.failed) return done(r.limitMessage ? 'limit' : 'error', { message: r.limitMessage || '' });
          var swapped = r.plan && r.plan[0] && r.plan[0].replaces;
          if (!swapped) return done('added');
          // one garment per body slot: the previous one left the look; say it (live region here, toast by the caller) and let it come back
          var entry = self.history.undo[self.history.undo.length - 1];
          var message = fill(self.slotCopy(0), { old: swapped.title, 'new': prod.title });
          self.announce(message);
          return done('replaced', { message: message, replacedTitle: swapped.title, title: prod.title, undo: function () { return self.undoEntry(entry); } });
        });
      }, function (err) { return done('error', { message: (err && err.message) || '' }); });
    }

    // ---- current look: load ----
    loadLook() {
      var self = this;
      if (this.viewer && (!this.brand || !this.modelId)) return; // the viewer waits for a brand and a chosen model
      var seq = ++this.reqLook;
      this.q('[data-fr-look-loading]').hidden = false;
      this.q('[data-fr-look-wrap]').hidden = true;
      if (this.tryon) this.tryon.setStatus('loading');
      if (this.viewer) return this.loadModelLook(seq);
      this.gw.getCurrentLook().then(function (res) {
        if (seq !== self.reqLook) return;
        var items = res.items || [];
        return Promise.all(items.map(function (it) {
          var known = self.catalog[it.productId];
          if (known) return known;
          return self.gw.getProduct(it.productId).then(function (p) {
            var prod = { id: p.id, title: p.title, brand: p.brand, price: p.price, assetId: p.assetId, slot: p.slot || it.slot, size: it.size };
            self.catalog[p.id] = prod;
            return prod;
          }, function () { return { id: it.productId, title: it.title, brand: '', price: '', assetId: it.assetId, slot: it.slot, size: it.size }; });
        })).then(function (products) {
          if (seq !== self.reqLook) return;
          self.look = products;
          self.lookError = false;
          self.looked = true;
          self.history = historyEmpty();
          self.savedLookId = null;
          self.lastRender = null;
          self.prevRender = null;
          self.renderNote = null;
          self.renderFailed = false;
          self.expanded = false;
          self.q('[data-fr-look-loading]').hidden = true;
          self.q('[data-fr-look-wrap]').hidden = false;
          self.render(true);
          self.consumeIntent();
          self.consumeArrival();
          self.consumeResume();
        });
      }, function (err) {
        if (seq !== self.reqLook) return;
        self.lookError = true;
        self.applyErrorCopy(self.lookEl.querySelector('.nc-current-look__state--error .nc-state-panel'), err);
        self.q('[data-fr-look-loading]').hidden = true;
        self.q('[data-fr-look-wrap]').hidden = false;
        self.render(false);
        if (self.tryon) self.tryon.setStatus('error');
        var retry = self.lookEl.querySelector('.nc-current-look__state--error [data-nc-retry]');
        if (retry) retry.focus({ preventScroll: true });
      });
    }

    // the toast of a notice may be asked for before nc-toast.js (deferred, later in the page) has run: wait for it, 2 s at most
    withToast(fn) {
      var tries = 0;
      (function tick() {
        if (window.NcToast) return fn(window.NcToast);
        if (++tries < 20) setTimeout(tick, 100);
      })();
    }

    // a look sent from another page ("Probar este look", nc-tryon-intent): read once, removed, applied over the loaded look
    consumeIntent() {
      if (this.viewer || this.intentDone) return;
      this.intentDone = true;
      var raw = null;
      try { raw = window.sessionStorage.getItem(INTENT_KEY); window.sessionStorage.removeItem(INTENT_KEY); } catch (e) { return; }
      var intent = raw && readIntent(raw, Date.now());
      if (!intent) return;
      this.intentTaken = true;
      var self = this;
      this.replaceLook(intent.ids, { title: intent.title }).then(function () {
        // focus on arrival: the "Mi look actual" heading (the toast says what happened, the live region too)
        var title = self.lookEl && self.lookEl.querySelector('.nc-current-look__title');
        if (title) { title.setAttribute('tabindex', '-1'); title.focus({ preventScroll: false }); }
      });
    }

    // N6: landing on the probador right after adding a garment elsewhere (PDP sheet, Corner, Hub, "Ver probador"): its tile in the slot row
    // (desktop) or its row in "Mi look actual" gets a Violet/600 outline for ~2 s (no layout shift; static under reduced motion, CSS) and the
    // focus moves to that list's heading, with one polite announcement. Read once; a look sent with "Probar este look" wins.
    consumeArrival() {
      if (this.viewer || this.arrivalDone || this.getAttribute('arrival') !== 'on') return;
      this.arrivalDone = true;
      var raw = null;
      try { raw = window.sessionStorage.getItem(ARRIVAL_KEY); window.sessionStorage.removeItem(ARRIVAL_KEY); } catch (e) { return; }
      if (this.intentTaken || !raw) return;
      var a = readArrival(raw, Date.now(), pagePath());
      var p = a && this.look.filter(function (x) { return x.id === a.id; })[0];
      if (!p) return;
      var self = this;
      this.arrivedId = p.id;
      this.markArrived();
      var tile = this.visibleOne('[data-fr-arrived]');
      var inSlots = tile && this.slotsEl && this.slotsEl.contains(tile);
      var heading = inSlots ? this.slotsEl.querySelector('.nc-fr__slots-title') : this.lookEl && this.lookEl.querySelector('.nc-current-look__title');
      // the garment must be seen, not under the sticky CREAR bar of mobile: off screen (or in the bottom 120 px) it comes to the middle,
      // instantly (no smooth scroll: reduced motion and §9); checked again once the catalogue above it has settled
      var reveal = function () {
        var el = self.visibleOne('[data-fr-arrived]');
        var box = el && el.getBoundingClientRect();
        if (box && (box.top < 0 || box.bottom > window.innerHeight - 120)) el.scrollIntoView({ block: 'center' });
      };
      reveal();
      window.setTimeout(reveal, 400);
      if (heading && heading.getClientRects().length) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      this.announce(fill(this.slotRowCopy(5), { name: p.title }));
      window.clearTimeout(this.arrivalTimer);
      this.arrivalTimer = window.setTimeout(function () { self.arrivedId = null; self.markArrived(); }, ARRIVAL_SHOW_MS);
    }

    // the outline follows re-renders of the slot row / look panel while it lasts
    markArrived() {
      var id = this.arrivedId;
      Array.prototype.forEach.call(this.querySelectorAll('[data-fr-arrived]'), function (el) { if (!id || el.getAttribute('data-id') !== id) el.removeAttribute('data-fr-arrived'); });
      if (!id) return;
      Array.prototype.forEach.call(this.querySelectorAll('.nc-fr__slot[data-id="' + id + '"], .nc-current-look__item[data-id="' + id + '"]'), function (el) { el.setAttribute('data-fr-arrived', ''); });
    }

    // "Probar este look": the current look becomes this look (one history entry: Deshacer brings the previous look back in one step)
    lookCopy(i) { return (this.m.lookCopy && this.m.lookCopy[i]) || DEFAULT_LOOK_COPY[i]; }

    replaceLook(ids, meta) {
      var self = this;
      var name = (meta && meta.title) || this.lookCopy(5);
      if (!this.canAdd()) return Promise.resolve({ status: 'unavailable' });
      return Promise.all(ids.map(function (id) {
        if (self.catalog[id]) return self.catalog[id];
        return self.gw.getProduct(id).then(function (p) {
          var prod = { id: p.id, title: p.title, brand: p.brand, price: p.price, assetId: p.assetId, slot: p.slot };
          self.catalog[p.id] = prod;
          return prod;
        }, function () { return null; });
      })).then(function (products) {
        var found = products.filter(Boolean);
        var missing = products.length - found.length;
        if (!found.length) { self.toast(self.msg(MSG.ADD_ERROR)); return { status: 'error' }; }
        var had = self.look.length;
        var ops = planReplace(self.look, found);
        if (!ops.length) {
          self.announce(self.lookCopy(2));
          self.withToast(function (T) { T.show({ tone: 'success', message: self.lookCopy(2) }); });
          return { status: 'exists' };
        }
        return self.apply(ops, 'replace', true).then(function (r) {
          if (!r.changed || r.failed) return { status: 'error' };
          var entry = self.history.undo[self.history.undo.length - 1];
          var leftOver = self.look.length > 0 && had > 0 && ops.some(function (o) { return o.op === 'remove'; });
          var message = fill(self.lookCopy(leftOver ? 0 : 1), { look: name });
          var detail = missing ? fill(self.lookCopy(4), { n: missing }) : undefined;
          self.announce(message + (detail ? ' ' + detail : ''));
          self.withToast(function (T) {
            var el = T.show(leftOver ? { tone: 'success', message: message, detail: detail, actionLabel: self.slotCopy(1), actionId: 'undo', duration: 12000 } : { tone: 'success', message: message, detail: detail });
            if (el && leftOver) el.addEventListener('nc-toast:action', function () {
              self.undoEntry(entry).then(function (ok) {
                if (!ok) return;
                self.announce(self.lookCopy(3));
                T.show({ tone: 'success', message: self.lookCopy(3) });
              });
            });
          });
          return { status: 'replaced' };
        });
      });
    }

    // viewer: the look of the chosen brand model (garments with size and price; no AI, no render)
    loadModelLook(seq) {
      var self = this;
      var modelId = this.modelId;
      this.gw.getBrandModelLook(this.brand, modelId).then(function (res) {
        if (seq !== self.reqLook) return;
        self.look = (res.items || []).map(function (it) { return { id: it.productId, title: it.title, brand: '', price: it.price, size: it.size, assetId: it.assetId }; });
        self.lookError = false;
        self.history = historyEmpty();
        self.expanded = false;
        self.q('[data-fr-look-loading]').hidden = true;
        self.q('[data-fr-look-wrap]').hidden = false;
        if (!(self.modelAssets && self.modelAssets[modelId])) self.showModelPhoto(res.assetId, modelId);
        self.render(true);
      }, function (err) {
        if (seq !== self.reqLook) return;
        self.lookError = true;
        self.applyErrorCopy(self.lookEl.querySelector('.nc-current-look__state--error .nc-state-panel'), err);
        self.q('[data-fr-look-loading]').hidden = true;
        self.q('[data-fr-look-wrap]').hidden = false;
        self.render(false);
        if (self.tryon) self.tryon.setStatus('error');
      });
    }

    // the viewer needs the brand slug, known only once the host has loaded the brand
    setBrand(slug) {
      this.brand = slug;
      if (this.viewer && this.ready && this.modelId) this.loadLook();
    }

    // error panel copy by failure kind (403, 409, 5xx, sign-in); network keeps the default copy
    applyErrorCopy(panel, err) {
      if (!panel) return;
      var title = panel.querySelector('.nc-state-panel__title');
      var body = panel.querySelector('.nc-state-panel__body');
      if (!title || !body) return;
      if (title.getAttribute('data-default') === null) { title.setAttribute('data-default', title.textContent); body.setAttribute('data-default', body.textContent); }
      var copy = errorCopy(err && err.kind, this.m.errors);
      title.textContent = copy ? copy.title : title.getAttribute('data-default');
      body.textContent = copy ? copy.body : body.getAttribute('data-default');
    }

    // ---- one render for every view of the look (AC-006) ----
    render(announce) {
      var self = this;
      var look = this.look;
      var fields = this.cfg.itemFields;
      var ul = this.lookEl.querySelector('.nc-current-look__items');
      ul.textContent = '';
      var tpl = this.q('template[data-fr-tpl="item"]');
      var plan = collapsePlan(look.length, this.cfg.lookLimit);
      look.forEach(function (p, n) {
        var li = tpl.content.querySelector('.nc-current-look__item').cloneNode(true);
        li.setAttribute('data-id', p.id);
        if (n >= plan.shown && plan.hidden) li.setAttribute('data-fr-extra', '');
        var name = li.querySelector('.nc-current-look__name');
        if (fields.indexOf('link') >= 0) {
          // "link": the name opens the product (event only, navigation is Phase B)
          var open = document.createElement('button');
          open.type = 'button';
          open.className = 'nc-fr__item-link';
          open.setAttribute('data-fr-open', p.id);
          open.setAttribute('aria-label', fill(self.m.fields[1] || '{name}', { name: p.title }));
          open.appendChild(document.createTextNode(p.title));
          open.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>');
          name.textContent = '';
          name.appendChild(open);
        } else {
          name.textContent = p.title;
        }
        var priceEl = li.querySelector('.nc-current-look__price');
        if (p.brand) {
          var brand = document.createElement('span');
          brand.className = 'nc-fr__row-brand';
          brand.textContent = p.brand;
          priceEl.parentNode.insertBefore(brand, priceEl);
        }
        priceEl.textContent = rowDetail(p, fields, self.m.fields[0]);
        var remove = li.querySelector('.nc-current-look__remove');
        remove.setAttribute('aria-label', self.m.look[5] + ': ' + p.title);
        if (self.pending[p.id]) remove.setAttribute('aria-busy', 'true');
        ul.appendChild(li);
        self.applyAsset(li.querySelector('.nc-current-look__thumb'), p.assetId, '', false);
      });
      var status = this.lookError ? 'error' : this.saving ? 'pending' : look.length ? 'populated' : 'empty';
      this.lookEl.setStatus(status);
      this.lookEl.updateCount();
      var title = this.lookEl.querySelector('[data-nc-look-title]');
      if (title) title.textContent = this.m.look[0];
      this.lookEl.classList.toggle('nc-fr__look-collapsed', !!plan.hidden && !this.expanded);

      // "Ver más" (PV-12): shown by the container query only when the panel is narrow
      var more = this.q('[data-fr-more]');
      if (more) {
        more.hidden = !plan.hidden || status === 'empty' || status === 'error';
        more.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
        more.querySelector('[data-fr-more-label]').textContent = this.expanded ? this.m.more[1] : fill(this.m.more[0], { n: plan.hidden });
      }

      var total = totalPrice(look.map(function (p) { return p.price; }));
      var totalText = total === null ? this.m.counts[2] : formatPrice(total);
      this.totalEl.querySelector('.nc-fr__total-label').textContent = this.m.counts[1];
      this.totalEl.querySelector('.nc-fr__total-value').textContent = totalText;
      this.totalEl.hidden = !this.has('itemFields', 'total') || status === 'empty' || status === 'error';
      if (this.noteEl) this.noteEl.hidden = status === 'empty' || status === 'error';

      var countText = fill(plural(this.m.counts[0], look.length), { n: look.length });
      var count = this.q('[data-fr-count]');
      if (count) count.textContent = countText;
      var dockCount = this.q('[data-fr-dock-count]');
      if (dockCount) dockCount.textContent = countText;

      var idle = this.q('.nc-tryon__message--idle');
      if (idle) idle.textContent = look.length ? this.m.tryon[3] : this.m.tryon[2];
      var keepCanvas = this.rendering || (this.lastRender && this.lastRender.status === 'ready') || this.renderFailed;
      if (this.tryon && !this.lookError && !this.modelPhoto && !keepCanvas && this.tryon.status !== 'idle') this.tryon.setStatus('idle', '');

      this.renderGarments();
      this.renderSlots();
      if (this.arrivedId) this.markArrived();
      this.syncTools();
      this.syncRender();
      var share = this.q('[data-fr-share] .nc-button');
      if (share && share.getAttribute('data-state') !== 'loading') share.removeAttribute('aria-disabled');
      this.emit('change', { items: look.slice(), pending: Object.assign({}, this.pending) });
      if (announce) this.announce(fill(this.m.tryon[8], { count: fill(plural(this.m.counts[0], look.length), { n: look.length }), total: totalText }));
    }

    // brand mode: the garments of the look laid out one by one over the model photo (decorative; the list is the accessible view)
    renderGarments() {
      var layer = this.q('[data-fr-garments]');
      if (!layer) return;
      var self = this;
      layer.hidden = this.cfg.mode !== 'brand' || !this.look.length || this.lookError;
      layer.textContent = '';
      if (layer.hidden) return;
      this.look.forEach(function (p) {
        var li = document.createElement('li');
        li.className = 'nc-fr__garment';
        li.setAttribute('data-id', p.id);
        var slot = document.createElement('span');
        slot.className = 'nc-fr__garment-media';
        li.appendChild(slot);
        layer.appendChild(li);
        self.applyAsset(slot, p.assetId, '', false);
      });
    }

    syncTools() {
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-fr-tool]'), function (btn) {
        var id = btn.getAttribute('data-fr-tool');
        var off = id === 'clear' ? !self.look.length : false;
        btn.setAttribute('aria-disabled', (off || (id === 'clear' && (self.saving || self.rendering))) ? 'true' : 'false');
      });
    }

    // ---- the one operation: optimistic, all views, rollback of what the gateway refuses ----
    changeLook(ops, quiet) {
      var self = this;
      if (this.saving || this.rendering) return Promise.resolve({ changed: false });
      var plan = planOps(this.look, ops);
      if (!plan.length) return Promise.resolve({ changed: false });
      plan.forEach(function (a) {
        if (a.op === 'add' && a.replaces) {
          var at = self.look.map(function (p) { return p.id; }).indexOf(a.replaces.id);
          if (at >= 0) self.look.splice(at, 1, a.product); else self.look.splice(a.index === undefined ? self.look.length : a.index, 0, a.product);
        } else if (a.op === 'add') self.look.splice(a.index === undefined ? self.look.length : a.index, 0, a.product); else self.look.splice(self.look.map(function (p) { return p.id; }).indexOf(a.product.id), 1);
        self.pending[a.product.id] = (self.pending[a.product.id] || 0) + 1;
        if (a.op === 'add') self.catalog[a.product.id] = self.catalog[a.product.id] || a.product;
      });
      this.savedLookId = null;
      this.lastRender = null;
      this.prevRender = null;
      this.renderNote = null;
      this.renderFailed = false;
      this.render(false);
      var limitMessage = null;
      var calls = plan.map(function (a) {
        var call = a.op === 'add' ? self.gw.addLookItem({ productId: a.product.id }) : self.gw.removeLookItem(a.product.id);
        return call.then(function () { return true; }, function (err) {
          if (!limitMessage && err && err.fieldErrors && err.fieldErrors.items) limitMessage = err.fieldErrors.items;
          return false;
        });
      });
      return Promise.all(calls).then(function (ok) {
        plan.forEach(function (a) { var id = a.product.id; self.pending[id] -= 1; if (self.pending[id] <= 0) delete self.pending[id]; });
        var failed = ok.some(function (v) { return !v; });
        if (failed) {
          self.look = rollbackOps(self.look, plan, ok);
          if (!quiet) self.toast(limitMessage || (plan.some(function (a, i) { return !ok[i] && a.op === 'add'; }) ? self.msg(MSG.ADD_ERROR) : self.msg(MSG.REMOVE_ERROR)));
        }
        self.render(true);
        return { changed: true, failed: failed, plan: plan, ok: ok, limitMessage: limitMessage };
      });
    }

    // ---- public entry points for the host's chooser: the change is recorded in the history when the gateway accepts it ----
    apply(ops, kind, quiet) {
      var self = this;
      return this.changeLook(ops, quiet).then(function (r) {
        if (kind && r.changed && !r.failed) self.history = historyPush(self.history, makeEntry(kind, r.plan));
        if (kind === 'add' && r.changed && !r.failed && ops[0] && ops[0].product) noteArrival(ops[0].product.id);
        self.syncTools();
        return r;
      });
    }

    toggle(product, on, quiet) {
      var self = this;
      if (product && product.price) product = Object.assign({}, product, { price: normalizePrice(product.price) }); // snap + rows: one price format
      return this.apply([{ op: on ? 'add' : 'remove', product: product, id: product.id }], on ? 'add' : 'remove', quiet).then(function (r) {
        // a chooser that adds without saying anything else (quiet = the caller shows its own notice, like addProduct) gets the replacement notice here
        if (!quiet && on && r.changed && !r.failed) self.noticeReplaced(r);
        return r;
      });
    }

    // one garment per body slot: say which garment left the look (live region + toast with "Deshacer" while that change is still the last one)
    noticeReplaced(r) {
      var self = this;
      var swapped = (r.plan || []).filter(function (a) { return a.replaces; })[0];
      if (!swapped) return;
      var entry = this.history.undo[this.history.undo.length - 1];
      var message = fill(this.slotCopy(0), { old: swapped.replaces.title, 'new': swapped.product.title });
      this.announce(message);
      var el = window.NcToast && window.NcToast.show({ tone: 'success', message: message, actionLabel: this.slotCopy(1), actionId: 'undo' });
      if (el) el.addEventListener('nc-toast:action', function () {
        self.undoEntry(entry).then(function (ok) { if (ok) self.announce(self.slotCopy(2)); });
      });
    }

    // "Look vaciado" + Deshacer (live region too); the undo works while the clear is still the last change of the history
    noticeCleared(entry) {
      var self = this;
      this.announce(this.msg(MSG.CLEARED));
      var el = window.NcToast && window.NcToast.show({ tone: 'success', message: this.msg(MSG.CLEARED), actionLabel: this.slotCopy(1), actionId: 'undo' });
      if (el) el.addEventListener('nc-toast:action', function () {
        self.undoEntry(entry).then(function (ok) { if (ok) self.announce(self.msg(MSG.RESTORED)); });
      });
    }

    // ---- row remove (x) ----
    onLookRemove(event) {
      event.preventDefault();
      var self = this;
      var id = event.detail.id;
      var product = this.look.filter(function (p) { return p.id === id; })[0];
      if (this.viewer || !product || this.pending[id]) return;
      var ids = this.look.map(function (p) { return p.id; });
      var at = ids.indexOf(id);
      var neighbor = ids[at + 1] || ids[at - 1] || null;
      this.apply([{ op: 'remove', id: id }], 'remove').then(function (r) {
        if (event.fromSlots) { self.focusSlotAfterRemove(r.failed ? id : neighbor); return; }
        var rowRemove = function (pid) { return self.lookEl.querySelector('.nc-current-look__item[data-id="' + pid + '"] .nc-current-look__remove'); };
        var target = r.failed ? rowRemove(id) : neighbor ? rowRemove(neighbor) : null;
        // a collapsed ("Ver más") row is not focusable: the focus falls back to the panel instead of the page
        if (target && target.getClientRects().length) target.focus();
        else { self.lookEl.setAttribute('tabindex', '-1'); self.lookEl.focus(); }
      });
    }

    // "Deshacer" of the replacement notice: undoes that history entry only while it is still the last one (anything newer wins)
    undoEntry(entry) {
      var top = this.history.undo[this.history.undo.length - 1];
      if (!entry || top !== entry) return Promise.resolve(false);
      return this.stepHistory('undo');
    }

    // one undo / redo step of the history; resolves true when the gateway accepted it
    stepHistory(id) {
      var self = this;
      if (this.saving) return Promise.resolve(false);
      var step = id === 'undo' ? historyUndo(this.history) : historyRedo(this.history);
      if (!step) return Promise.resolve(false);
      return this.changeLook(id === 'undo' ? undoOps(step.entry) : redoOps(step.entry)).then(function (r) {
        var ok = !!(r.changed && !r.failed);
        if (ok) self.history = step.history;
        self.syncTools();
        return ok;
      });
    }

    // copy of the one-garment-per-slot notice: data-msg-slots = replaced ('{old}', '{new}')|undo|restored|change|change aria ('{old}', '{name}')
    slotCopy(i) { return this.m.slots[i] || ['Sustituimos «{old}» por «{new}»: solo cabe una prenda de este tipo.', 'Deshacer', 'Hemos vuelto a poner la prenda anterior', 'Cambiar', 'Cambiar «{old}» por «{name}»'][i]; }

    // ---- side tools ----
    onTool(id) {
      var self = this;
      var go = this.emit('tool', { id: id }, true);
      if (!go) return;
      if (id === 'clear') {
        if (this.saving) return;
        var items = this.look.slice();
        if (!items.length) return;
        // Limpiar asks nothing: the look empties and a toast "Look vaciado" offers Deshacer (the slot rule's undo + toast pattern)
        this.apply(items.map(function (p) { return { op: 'remove', id: p.id }; }), 'remove').then(function (r) {
          var first = self.visibleOne('[data-fr-tool="clear"]');
          if (first && first.getAttribute('aria-disabled') !== 'true') first.focus(); else if (self.lookEl) { self.lookEl.setAttribute('tabindex', '-1'); self.lookEl.focus({ preventScroll: true }); }
          if (!r || !r.changed || r.failed) return;
          self.noticeCleared(self.history.undo[self.history.undo.length - 1]);
        });
        return;
      }
      this.toast(this.msg(MSG.TOOL)); // compare and details: no contract yet (UNKNOWN), event only
    }

    onToolKeydown(event) {
      // arrows move inside a tools column (roving focus without leaving the tab order)
      var btn = event.target.closest && event.target.closest('[data-fr-tool]');
      if (!btn || ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(event.key) < 0) return;
      var group = btn.closest('[data-fr-tools]');
      var all = Array.prototype.filter.call(group.querySelectorAll('[data-fr-tool]'), function (b) { return !b.hidden; });
      var i = all.indexOf(btn);
      var forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
      var next = event.key === 'Home' ? 0 : event.key === 'End' ? all.length - 1 : (i + (forward ? 1 : -1) + all.length) % all.length;
      event.preventDefault();
      all[next].focus();
    }

    // canvas views: no preset configures any today (the rail was removed, UX audit 2026-10-24); a host that adds one gets the notice
    onViewTool() {
      this.toast(this.msg(MSG.TOOL));
    }

    // ---- main button (save in the workspace, connect in the brand screens), favorite heart ----
    onMain() {
      if (this.has('actions', 'save')) this.onSave(); else if (this.has('actions', 'connect')) this.onConnect();
    }

    onConnect() {
      var ok = this.emit('connect', { items: this.look.slice() }, true);
      if (ok) this.toast(this.msg(MSG.CONNECT));
    }

    // the look heart has no gateway meaning yet (saving a look is the "save" action): undo the press and say so
    onFavorite(event) {
      var heart = this.lookEl.querySelector('.nc-current-look__header .nc-icon-button');
      if (heart) heart.setAttribute('aria-pressed', 'false');
      this.toast(this.msg(MSG.TOOL));
      event.stopPropagation();
    }

    onSave() {
      var self = this;
      if (this.saving) return;
      if (!this.look.length) { this.toast(this.msg(MSG.EMPTY)); return; }
      // D-1: saving a look needs an account; the access sheet opens (nc-auth.js) and nothing is saved
      if (window.NcAuth && !window.NcAuth.isIn()) { this.emit('signin', { reason: 'save', resume: 'guardar' }, true); return; }
      this.saving = true;
      this.render(false);
      var btn = this.visibleOne('[data-fr-slots-save] .nc-button') || this.lookEl.querySelector('.nc-current-look__save .nc-button');
      var renderId = this.lastRender && this.lastRender.status === 'ready' ? this.lastRender.renderId : null;
      this.gw.saveLook(renderId ? { renderId: renderId } : undefined).then(function (res) {
        self.saving = false;
        self.render(false);
        self.savedLookId = res.lookId;
        self.flashButton(btn, 'success', self.m.save[0]);
        self.announce(self.m.save[0]);
        self.emit('save', res.renderAssetId ? { lookId: res.lookId, renderAssetId: res.renderAssetId } : { lookId: res.lookId });
      }, function () {
        self.saving = false;
        self.render(false);
        self.flashButton(btn, 'error', self.m.save[1]);
        self.toast(self.msg(MSG.SAVE_ERROR));
        if (btn) btn.focus();
      });
    }

    flashButton(btn, state, text) {
      if (!btn) return;
      var overlay = btn.querySelector('.nc-button__overlay--' + state);
      if (overlay && overlay.lastChild && overlay.lastChild.nodeType === 3) overlay.lastChild.nodeValue = text;
      btn.setAttribute('data-state', state);
      window.setTimeout(function () { if (btn.getAttribute('data-state') === state) btn.setAttribute('data-state', 'default'); }, 4000);
    }

    onShare() {
      var self = this;
      var btn = this.q('[data-fr-share] .nc-button');
      if (!btn || btn.getAttribute('data-state') === 'loading') return;
      if (!this.savedLookId) { this.toast(this.msg(MSG.SAVE_FIRST)); return; }
      var lookId = this.savedLookId;
      btn.setAttribute('data-state', 'loading');
      // B7 (D-IA): a shared look that shows a generated image carries the AI label in the shared text, never the bare link
      var ai = !!(this.lastRender && this.lastRender.status === 'ready' && !this.isDemo(this.lastRender)) ? this.rt(10) : '';
      this.gw.shareLook({ lookId: lookId }).then(function (res) {
        var url = res.url;
        self.emit('share', { lookId: lookId, url: url, aiGenerated: !!ai });
        if (navigator.share) {
          return navigator.share(ai ? { text: ai, url: url } : { url: url }).then(function () { self.flashButton(btn, 'success', self.m.share[2]); }, function (e) {
            if (e && e.name === 'AbortError') btn.setAttribute('data-state', 'default'); else throw e;
          });
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          return navigator.clipboard.writeText(ai ? ai + ' ' + url : url).then(function () {
            self.flashButton(btn, 'success', self.m.share[2]);
            self.toast(self.m.share[4], 'success');
          }, function () { self.showLink(btn, url); });
        }
        self.showLink(btn, url);
      }).then(null, function () {
        self.flashButton(btn, 'error', self.m.share[3]);
        self.toast(self.msg(MSG.SHARE_ERROR));
        btn.focus();
      });
    }

    // last fallback of the share: the link itself, readable and selectable in the toast
    showLink(btn, url) {
      this.flashButton(btn, 'success', this.m.share[2]);
      window.NcToast.show({ tone: 'success', message: this.m.share[2], detail: url });
    }

    // ---- add: the chooser belongs to the host; the component only brings the user to it ----
    // ---- the slot row of the builder (looks mode, desktop): one tile per garment of the look (thumbnail, brand, title, price, ×) and ONE
    // dashed "+" tile while the look has room (N3 of the re-validation: not one empty tile per free slot); with no garment the row is one
    // line, "Tu look (0/5) · elige prendas abajo", so the catalogue's first row is on the first screen. The "+" brings the person to the
    // catalogue (onAdd); onSlotAdd(value) still selects and focuses a category chip for a caller that names a slot ----
    setupSlots() {
      var self = this;
      this.slotsEl = this.q('[data-fr-slots]');
      if (!this.slotsEl) return;
      this.slotsList = this.slotsEl.querySelector('[data-fr-slots-list]');
      this.slotsCount = this.slotsEl.querySelector('[data-fr-slots-count]');
      this.slotsSave = this.slotsEl.querySelector('[data-fr-slots-save]');
      // the host's category chips arrive with its first search: the empty tiles follow them
      var chips = this.q('[data-fr-chooser] [data-fr-slot-chips]');
      if (chips && typeof MutationObserver === 'function') new MutationObserver(function () { self.renderSlots(); }).observe(chips, { childList: true });
    }

    slotRowCopy(i) { return (this.m.slotRow && this.m.slotRow[i]) || SLOT_ROW_DEFAULTS[i]; }

    renderSlots() {
      var ul = this.slotsList;
      if (!ul) return;
      var self = this;
      var look = this.look;
      var max = this.rf && this.rf.max ? this.rf.max : 5;
      var fields = this.cfg.itemFields;
      ul.textContent = '';
      look.forEach(function (p) {
        var li = document.createElement('li');
        li.className = 'nc-fr__slot';
        li.setAttribute('data-id', p.id);
        var thumb = document.createElement('span');
        thumb.className = 'nc-fr__slot-thumb';
        thumb.setAttribute('aria-hidden', 'true');
        li.appendChild(thumb);
        var copy = document.createElement('span');
        copy.className = 'nc-fr__slot-copy';
        if (p.brand) {
          var brand = document.createElement('span');
          brand.className = 'nc-fr__slot-brand';
          brand.textContent = p.brand;
          copy.appendChild(brand);
        }
        // the title is the tile's accessible text and tooltip, not painted: the stage's garment strip is where the look's garments are
        // listed with their names (Javi 2026-10-25: never the same garment list twice on screen)
        li.setAttribute('title', p.title);
        var name = document.createElement('span');
        name.className = 'nc-fr__slot-name nc-visually-hidden';
        name.textContent = p.title;
        copy.appendChild(name);
        var detail = rowDetail(p, fields, self.m.fields[0]);
        if (detail) {
          var price = document.createElement('span');
          price.className = 'nc-fr__slot-price';
          price.textContent = detail;
          copy.appendChild(price);
        }
        li.appendChild(copy);
        if (!self.viewer) {
          var x = document.createElement('button');
          x.type = 'button';
          x.className = 'nc-fr__slot-remove';
          x.setAttribute('data-fr-slot-remove', p.id);
          x.setAttribute('aria-label', self.m.look[5] + ': ' + p.title);
          if (self.pending[p.id]) x.setAttribute('aria-busy', 'true');
          x.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg>';
          li.appendChild(x);
        }
        ul.appendChild(li);
        self.applyAsset(thumb, p.assetId, '', false);
      });
      var room = Math.max(0, max - look.length);
      this.slotsEl.toggleAttribute('data-empty', !look.length);
      var line = this.slotsEl.querySelector('[data-fr-slots-hint]');
      if (line) line.textContent = this.slotRowCopy(4);
      if (!this.viewer && room > 0 && look.length) {
        var f = { value: '', label: this.slotRowCopy(3) };
        var li = document.createElement('li');
        li.className = 'nc-fr__slot nc-fr__slot--empty';
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'nc-fr__slot-add';
        b.setAttribute('data-fr-slot-add', f.value);
        b.innerHTML = '<span class="nc-fr__slot-plus" aria-hidden="true"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M5 12h14M12 5v14"/></svg></span>';
        var label = document.createElement('span');
        label.className = 'nc-fr__slot-label';
        label.textContent = f.label;
        b.appendChild(label);
        li.appendChild(b);
        ul.appendChild(li);
      }
      this.syncSlotsCount();
      if (this.slotsSave) this.slotsSave.hidden = !look.length || this.lookError || !(this.has('actions', 'save') || this.has('actions', 'connect'));
      if (this.arrivedId) this.markArrived();
    }

    // "(n/5)": the limit is the try-on config's (gateway), so until it is known the count goes alone
    syncSlotsCount() {
      if (!this.slotsCount) return;
      var max = this.rf && this.rf.max;
      this.slotsCount.textContent = fill(max ? this.slotRowCopy(1) : '({n})', { n: this.look.length, max: max || '' });
    }

    // a "+" tile: the matching category chip of the host is selected (its products show) and takes the focus; without chips, the catalogue
    onSlotAdd(value) {
      var chip = value ? this.q('[data-fr-chooser] [data-fr-slot-chips] nc-chip[data-value="' + value + '"] .nc-chip__main') : null;
      if (!chip) { this.onAdd(); return; }
      if (chip.getAttribute('aria-pressed') !== 'true') chip.click();
      chip.focus();
    }

    // after a remove from the slot row the focus stays in the row: the neighbour's ×, else the row title (never the page)
    focusSlotAfterRemove(id) {
      var target = id && this.slotsList && this.slotsList.querySelector('[data-fr-slot-remove="' + id + '"]');
      if (target && target.getClientRects().length) { target.focus(); return; }
      var title = this.slotsEl && this.slotsEl.querySelector('.nc-fr__slots-title');
      if (title && title.getClientRects().length) title.focus({ preventScroll: true });
    }

    // the one of several twins (desktop slot row / look panel) that is on screen
    visibleOne(sel) {
      var all = this.querySelectorAll(sel);
      for (var i = 0; i < all.length; i++) if (all[i].getClientRects().length) return all[i];
      return all[0] || null;
    }

    onAdd() {
      var ok = this.emit('open', { kind: 'chooser', id: '' }, true);
      if (!ok) return;
      var target = this.q('[data-fr-chooser] [data-fr-focus]');
      if (target) { target.setAttribute('tabindex', '-1'); target.scrollIntoView({ block: 'start' }); target.focus({ preventScroll: true }); }
    }

    // ---- clicks ----
    onClick(event) {
      var t = event.target;
      var tool = t.closest('[data-fr-tool]');
      if (tool) {
        if (tool.getAttribute('aria-disabled') === 'true') return;
        this.onTool(tool.getAttribute('data-fr-tool'));
        return;
      }
      var modelOpen = t.closest('[data-fr-model-open]');
      if (modelOpen) { this.changeModel(modelOpen); return; }
      var trigger = t.closest('[data-fr-model-sheet]');
      // preset models hidden until integrated (PENDING in nc-fitting-room-presets.js): the person icon opens the photo dialog directly
      if (trigger && !this.cfg.modelPresets && this.cfg.modelSource === 'upload') { this.changeModel(trigger); return; }
      if (trigger) {
        if (this.sheet && this.sheet.open) {
          this.sheet.open(trigger);
          var tile = this.q('[data-fr-sheet] .nc-fr__model-tile[aria-pressed="true"]') || this.q('[data-fr-sheet] .nc-fr__model-tile');
          if (tile) tile.focus();
        }
        return;
      }
      var modelTile = t.closest('.nc-fr__model-tile');
      if (modelTile) { this.onModelTile(modelTile); return; }
      var pmTile = t.closest('.nc-fr__pm-tile');
      if (pmTile && this.uploadOpen) { this.choosePresetModel(pmTile.getAttribute('data-fr-pm-id'), 0); return; }
      var act = t.closest('[data-fr-act]');
      // the whole drop area opens the file picker (Javi, 2026-10-24: people click the dashed box, not the small link inside it)
      if (!act && t.closest('[data-fr-drop]') && this.uploadOpen && t !== this.fileInput && t !== this.cameraInput) { if (this.fileInput) this.fileInput.click(); return; }
      if (act) {
        var what = act.getAttribute('data-fr-act');
        if (what === 'choose' || what === 'choose-link') { if (this.fileInput) this.fileInput.click(); return; }
        if (what === 'camera') { if (this.cameraInput) this.cameraInput.click(); return; }
        if (what === 'use') { var ub = act.querySelector('.nc-button') || act; if (!ub.disabled) { this.keyCreate = event.detail === 0; this.useUploadedPhoto(); } return; }
        if (what === 'remove') { this.removeCurrentPhoto(); return; }
        if (what === 'cancel') { this.closeUpload(); return; }
      }
      if (t.closest('[data-fr-retry]')) { this.generate(); return; }
      if (t.closest('[data-fr-use-last]')) { this.useLastResult(); return; }
      if (t.closest('[data-fr-share] .nc-button')) { event.preventDefault(); this.onShare(); return; }
      if (t.closest('[data-fr-add]')) { this.onAdd(); return; }
      var slotRemove = t.closest('[data-fr-slot-remove]');
      if (slotRemove) { this.onLookRemove({ preventDefault: function () {}, detail: { id: slotRemove.getAttribute('data-fr-slot-remove') }, fromSlots: true }); return; }
      var slotAdd = t.closest('[data-fr-slot-add]');
      if (slotAdd) { this.onSlotAdd(slotAdd.getAttribute('data-fr-slot-add')); return; }
      if (t.closest('[data-fr-slots-save] .nc-button')) { this.onMain(); return; }
      var more = t.closest('[data-fr-more]');
      if (more) { this.expanded = !this.expanded; this.render(false); more.focus(); return; }
      var open = t.closest('[data-fr-open]');
      if (open) { this.emit('open', { kind: 'product', id: open.getAttribute('data-fr-open') }, true); }
    }
  }

  customElements.define('nc-fitting-room', NcFittingRoomElement);
})();
